import AdminReference from '../components/AdminReference';
// src/pages/admin/Users.js

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Checkbox,
  ListItemText,
  Step,
  StepLabel,
  Stepper,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import UploadRoundedIcon from '@mui/icons-material/UploadRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import PasswordRoundedIcon from '@mui/icons-material/PasswordRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import DashboardLayout from '../components/DashboardLayout';
import UserProfileDialog from '../components/UserProfileDialog';
import { AuthContext } from '../auth/AuthContext';
import {
  changeUserPassword,
  createAdminStudent,
  createTutorProfileShareLink,
  deleteUser,
  disableTutorProfileShareLink,
  downloadTutorProfileDocument,
  exportUsersFile,
  fetchTutorApplicationDocuments,
  fetchPortalRequirements,
  fetchAdminStudentTutorEligibility,
  fetchUsers,
  importUsersFile,
  lockUserAccount,
  registerUser,
  resendAdminStudentActivation,
  resendWelcomeEmail,
  setUserSubjects,
  unlockUserAccount,
  updateUser,
} from '../services/user';
import { downloadTutorImportFile } from '../services/tutorImports';
import { fetchSubjects } from '../services/subject';
import {
  assignAdminBookingStudentPackage,
  changeAdminBookingParentPassword,
  createAdminBookingParent,
  createAdminBookingPaymentLink,
  createAdminBookingStudent,
  deactivateAdminBookingParent,
  fetchAdminBookingStudentPackages,
  fetchAdminLinkableLmsStudents,
  linkAdminLmsStudentToBookingParent,
  lockAdminBookingParent,
  resendAdminBookingParentWelcome,
  unlockAdminBookingParent,
  updateAdminBookingParent,
} from '../services/booking';
import {
  COUNTRY_OPTIONS,
  PHONE_CODE_SEARCH_OPTIONS,
  combinePhoneValue,
  getDefaultPhoneCodeForCountry,
  splitPhoneValue,
} from '../utils/phoneMetadata';
import { filterSubjectsForCurriculum } from '../utils/curriculumSubjectCatalog';
import api from '../services/api';

const gradeOptions = ['R', ...Array.from({ length: 12 }, (_, idx) => String(idx + 1))];
const tutoringChoices = ['online', 'in-person'];
const tutorPortalOptions = [
  { value: 'tutor', label: 'General Programme' },
  { value: 'math', label: 'Math Programme' },
  { value: 'matric', label: 'Matric Programme' },
];
const roleOptions = [
  { value: 'student', label: 'Student' },
  { value: 'tutor', label: 'Tutor' },
  { value: 'admin', label: 'Admin' },
  { value: 'superadmin', label: 'Superadmin' },
];
const studentStepLabels = ['Basic info', 'Academic profile', 'Delivery & location', 'Preferences', 'Review'];
const studentProgramOptions = [
  { value: 'general', label: 'General Tutoring' },
  { value: 'math', label: 'Mathematics Classes' },
  { value: 'matric', label: 'Matric Programme' },
];
const tutorStepLabels = ['Basic info', 'Program & expertise', 'Address', 'Preferences', 'Review'];
const curriculumOptions = ['CAPS', 'IEB', 'CAMBRIDGE', 'PEARSON EDEXCEL', 'IB'];
const bookingDeliveryOptions = ['online', 'in-person'];
const bookingLanguageOptions = ['English', 'Afrikaans', 'isiZulu', 'isiXhosa', 'Sepedi', 'Sesotho', 'Setswana', 'Tshivenda', 'French', 'Portuguese'];
const bookingTeachingStyleOptions = ['Structured lessons', 'Exam preparation', 'Concept reinforcement', 'Homework support', 'Interactive learning', 'Past-paper practice'];
const bookingLearningSupportOptions = ['ADHD', 'ASD', 'Learning disabilities', 'Anxiety or stress support', 'Home-schooled', 'None'];
const bookingSpecialNeedsOptions = ['ADHD', 'ASD', 'Learning disabilities', 'Anxiety or stress disorders', 'Home schooled', 'None of the above'];
const bookingDaysOptions = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const bookingTimeSlotOptions = ['Morning', 'Afternoon', 'Evening', 'Weekdays', 'Weekends'];
const uniqueItems = (arr) => Array.from(new Set(arr));
const cleanText = (value) => (typeof value === 'string' ? value.trim() : '');
const splitAdminDisplayName = (value) => {
  const parts = cleanText(value).split(/\s+/).filter(Boolean);
  return {
    name: parts[0] || '',
    surname: parts.slice(1).join(' '),
  };
};
const splitListInput = (value) => {
  if (Array.isArray(value)) return uniqueItems(value.map(cleanText).filter(Boolean));
  if (typeof value !== 'string') return [];
  return uniqueItems(
    value
      .split(/[\s,;]+/)
      .map((item) => item.trim())
      .filter(Boolean),
  );
};

const splitFilterList = (value) => {
  if (Array.isArray(value)) return uniqueItems(value.map((entry) => cleanText(String(entry || ''))).filter(Boolean));
  if (typeof value !== 'string') return [];
  const trimmed = value.trim();
  if (!trimmed) return [];
  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) return splitFilterList(parsed);
  } catch {
    // Fall through to delimiter parsing for legacy comma/semicolon fields.
  }
  return uniqueItems(trimmed.split(/[,;]+/).map(cleanText).filter(Boolean));
};

const getTutorPostalCodes = (user) =>
  uniqueItems([
    ...splitFilterList(user?.postalCodes),
    cleanText(user?.postalCode),
    cleanText(user?.areaCode),
  ].filter(Boolean));
const getTutorGrades = (user) => splitFilterList(user?.gradeLevels);
const getTutorCurricula = (user) => splitFilterList(user?.curricula);
const getTutorDeliveryMethods = (user) => splitFilterList(user?.tutoringOptions);
const getTutorSubjectLabels = (user, subjectsCatalog = []) => {
  const labels = [...splitFilterList(user?.subjectFocus)];
  const subjectRefs = Array.isArray(user?.subjectIds) ? user.subjectIds : Array.isArray(user?.subjects) ? user.subjects : [];
  subjectRefs.forEach((item) => {
    if (item && typeof item === 'object') {
      labels.push(cleanText(item.name));
      return;
    }
    const match = subjectsCatalog.find((subject) => String(subject._id) === String(item));
    if (match) labels.push(cleanText(match.name));
  });
  return uniqueItems(labels.filter(Boolean));
};
const formatFilterList = (values, fallback = '—', limit = 3) => {
  const list = uniqueItems((values || []).map(cleanText).filter(Boolean));
  if (!list.length) return fallback;
  const visible = list.slice(0, limit).join(', ');
  return list.length > limit ? `${visible} +${list.length - limit}` : visible;
};

const isBookingParentMirror = (user) =>
  String(user?.role || '').toLowerCase() === 'parent' || String(user?.sourceType || '').toLowerCase() === 'booking_parent';

const defaultTutorFilters = {
  curricula: [],
  subjects: [],
  grades: [],
  deliveryMethods: [],
  provinces: [],
  suburbs: [],
  towns: [],
  areaCodes: [],
  countries: [],
  statuses: [],
  postalCodes: [],
  postalCode: '',
  area: '',
};

const parseProfileJson = (value, fallback) => {
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const tutorProficiencyOptions = ['Native', 'Fluent', 'Conversational'];
const tutorDaysOptions = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const tutorLanguageOptions = ['English', 'Afrikaans', 'isiZulu', 'isiXhosa', 'Sepedi', 'Sesotho', 'Setswana', 'siSwati', 'Tshivenda', 'Xitsonga', 'isiNdebele'];
const tutorInternationalLanguageOptions = ['French', 'Portuguese', 'Spanish', 'Arabic', 'Mandarin Chinese', 'Hindi', 'Urdu', 'German', 'Russian', 'Swahili'];
const emptySubjectExperience = () => ({ subject: '', years: '' });
const emptyLanguage = () => ({ language: '', proficiency: '' });
const emptyAvailability = () => ({
  daysAvailable: [],
  timeZone: 'Africa/Johannesburg',
  timeSlots: [],
  hasWeekendAvailability: false,
  hasEmergencyExamSupport: false,
});
const emptyReadiness = () => ({
  hasStableInternet: false,
  hasLaptopOrDesktop: false,
  hasWebcam: false,
  hasQuietTeachingEnvironment: false,
  hasWritingTablet: false,
});

const normalizeTutorEditorValue = (value, fallback) => {
  const parsed = parseProfileJson(value, fallback);
  return parsed && typeof parsed === 'object' ? parsed : fallback;
};

const GeneralTutorProfileFields = ({ formState, setFormState, subjects = [] }) => {
  const update = (key, value) => setFormState((prev) => ({ ...prev, [key]: value }));
  const subjectExperience = Array.isArray(formState.subjectExperience) ? formState.subjectExperience : [];
  const languages = normalizeTutorEditorValue(formState.languagesSpoken, { southAfrican: [], international: [] });
  const availability = normalizeTutorEditorValue(formState.availabilitySchedule, emptyAvailability());
  const readiness = normalizeTutorEditorValue(formState.internetDeviceReadiness, emptyReadiness());
  const subjectNames = subjects.map((subject) => subject.name).filter(Boolean);
  const updateRow = (key, index, field, value) => {
    const rows = Array.isArray(formState[key]) ? [...formState[key]] : [];
    rows[index] = { ...(rows[index] || {}), [field]: value };
    update(key, rows);
  };
  const updateLanguage = (group, index, field, value) => {
    const next = { southAfrican: [...(languages.southAfrican || [])], international: [...(languages.international || [])] };
    next[group][index] = { ...(next[group][index] || emptyLanguage()), [field]: value };
    update('languagesSpoken', next);
  };
  const addLanguage = (group) => update('languagesSpoken', {
    southAfrican: group === 'southAfrican' ? [...(languages.southAfrican || []), emptyLanguage()] : [...(languages.southAfrican || [])],
    international: group === 'international' ? [...(languages.international || []), emptyLanguage()] : [...(languages.international || [])],
  });
  const removeLanguage = (group, index) => update('languagesSpoken', {
    southAfrican: (languages.southAfrican || []).filter((_, rowIndex) => group !== 'southAfrican' || rowIndex !== index),
    international: (languages.international || []).filter((_, rowIndex) => group !== 'international' || rowIndex !== index),
  });
  const updateAvailability = (field, value) => update('availabilitySchedule', { ...availability, [field]: value });
  const updateReadiness = (field, value) => update('internetDeviceReadiness', { ...readiness, [field]: value });
  const languageRows = (group, options) => (
    <Stack spacing={1}>
      {(languages[group] || []).map((entry, index) => (
        <Grid container spacing={1} key={`${group}-${index}`}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{group === 'southAfrican' ? 'South African language' : 'International language'}</InputLabel>
              <Select label={group === 'southAfrican' ? 'South African language' : 'International language'} value={entry.language || ''} onChange={(event) => updateLanguage(group, index, 'language', event.target.value)}>
                {options.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={10} md={4}>
            <FormControl fullWidth>
              <InputLabel>Proficiency</InputLabel>
              <Select label="Proficiency" value={entry.proficiency || ''} onChange={(event) => updateLanguage(group, index, 'proficiency', event.target.value)}>
                {tutorProficiencyOptions.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={2} md={2}><Button color="error" onClick={() => removeLanguage(group, index)}>Remove</Button></Grid>
        </Grid>
      ))}
      <Button variant="outlined" onClick={() => addLanguage(group)}>Add language</Button>
    </Stack>
  );

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2">Subject experience</Typography>
      {subjectExperience.map((entry, index) => (
        <Grid container spacing={1} key={`experience-${index}`}>
          <Grid item xs={12} md={8}><Autocomplete freeSolo options={subjectNames} value={entry.subject || ''} onChange={(_, value) => updateRow('subjectExperience', index, 'subject', value || '')} onInputChange={(_, value) => updateRow('subjectExperience', index, 'subject', value || '')} renderInput={(params) => <TextField {...params} label="Subject" />} /></Grid>
          <Grid item xs={8} md={2}><TextField label="Years" type="number" fullWidth value={entry.years || ''} onChange={(event) => updateRow('subjectExperience', index, 'years', event.target.value)} /></Grid>
          <Grid item xs={4} md={2}><Button color="error" onClick={() => update('subjectExperience', subjectExperience.filter((_, rowIndex) => rowIndex !== index))}>Remove</Button></Grid>
        </Grid>
      ))}
      <Button variant="outlined" onClick={() => update('subjectExperience', [...subjectExperience, emptySubjectExperience()])}>Add subject experience</Button>
      <Typography variant="subtitle2">Languages spoken</Typography>
      <Typography variant="caption" color="text.secondary">Add each language with its proficiency.</Typography>
      {languageRows('southAfrican', tutorLanguageOptions)}
      {languageRows('international', tutorInternationalLanguageOptions)}
      <Typography variant="subtitle2">Availability</Typography>
      <FormControl fullWidth><InputLabel>Days available</InputLabel><Select multiple label="Days available" value={availability.daysAvailable || []} onChange={(event) => updateAvailability('daysAvailable', event.target.value)}>{tutorDaysOptions.map((day) => <MenuItem key={day} value={day}><Checkbox checked={(availability.daysAvailable || []).includes(day)} /><ListItemText primary={day} /></MenuItem>)}</Select></FormControl>
      <TextField label="Time zone" fullWidth value={availability.timeZone || ''} onChange={(event) => updateAvailability('timeZone', event.target.value)} />
      <TextField label="Time slots" fullWidth helperText="Example: 16:00-18:00, 18:00-20:00" value={(availability.timeSlots || []).join(', ')} onChange={(event) => updateAvailability('timeSlots', splitListInput(event.target.value))} />
      <FormControlLabel control={<Checkbox checked={Boolean(availability.hasWeekendAvailability)} onChange={(event) => updateAvailability('hasWeekendAvailability', event.target.checked)} />} label="Weekend availability" />
      <FormControlLabel control={<Checkbox checked={Boolean(availability.hasEmergencyExamSupport)} onChange={(event) => updateAvailability('hasEmergencyExamSupport', event.target.checked)} />} label="Emergency exam support" />
      <Typography variant="subtitle2">Internet and device readiness</Typography>
      {[["hasStableInternet", 'Stable internet'], ['hasLaptopOrDesktop', 'Laptop / desktop'], ['hasWebcam', 'Webcam'], ['hasQuietTeachingEnvironment', 'Quiet teaching environment'], ['hasWritingTablet', 'Writing tablet (optional)']].map(([field, label]) => <FormControlLabel key={field} control={<Checkbox checked={Boolean(readiness[field])} onChange={(event) => updateReadiness(field, event.target.checked)} />} label={label} />)}
    </Stack>
  );
};

const requiresSouthAfricaForCaps = (country, curricula) => {
  const selectedCurricula = splitListInput(curricula);
  const hasCaps = selectedCurricula.some((entry) => cleanText(entry).toUpperCase() === 'CAPS');
  return hasCaps && cleanText(country).toLowerCase() !== 'south africa';
};

const parseStudentGrade = (value) => {
  if (value === undefined || value === null || value === '') return NaN;
  if (String(value).trim().toUpperCase() === 'R') return 0;
  const raw = Number(value);
  return Number.isFinite(raw) ? raw : NaN;
};

const formatStudentGrade = (value) => Number(value) === 0 ? 'Grade R' : `Grade ${value}`;

const getApiErrorPayload = (error) => {
  const details = error?.details;
  const fallback = error?.response?.data;
  return details && typeof details === 'object' ? details : fallback && typeof fallback === 'object' ? fallback : null;
};

const mapBackendErrorToFieldHints = (error) => {
  const payload = getApiErrorPayload(error);
  const message = payload?.error || payload?.message || error?.message || 'Failed to save user.';
  const lowered = String(message).toLowerCase();
  const hints = {};
  const requirementMessages = [];

  if (payload?.field) {
    hints[payload.field] = message;
  }

  if (Array.isArray(payload?.requirements) && payload.requirements.length) {
      payload.requirements.forEach((item) => {
        if (!item || typeof item !== 'object') return;
        const fields = Array.isArray(item.missingFields) ? item.missingFields : [];
      if (fields.includes('tutoringOptions')) {
        hints.tutoringOptions = 'Select at least one delivery method';
      }
      if (item.message) {
        requirementMessages.push(item.message);
      }
    });
  }

  if (Array.isArray(payload?.invalidCurricula) && payload.invalidCurricula.length) {
    hints.curricula = `Remove or change these curricula: ${payload.invalidCurricula.join(', ')}`;
    const country = payload.country || null;
    if (country) {
      hints.country = `Selected country (${country}) is not valid for the selected curricula.`;
    }
  }

  if (lowered.includes('invalid curricula')) {
    hints.curricula = 'One or more selected curricula are not valid.';
  }

  if (lowered.includes('already exists')) {
    hints.email = 'This email address is already in use.';
  }
  if (lowered.includes('grade is required for general programme students')) {
    hints.grade = 'Grade is required for General programme students.';
  }
  if (
    lowered.includes('grade must be an integer between 1 and 12') ||
    lowered.includes('general programme students must have a grade between 1 and 12') ||
    lowered.includes('mathematics programme grades must be between 1 and 12') ||
    lowered.includes('matric programme grades must be between 7 and 12')
  ) {
    hints.grade = hints.grade || message;
  }

  if (lowered.includes('one or more subject ids are invalid')) {
    hints.subjectIds = 'One or more selected subjects are no longer available.';
  }

  if (lowered.includes('select at least one curriculum before assigning subjects')) {
    hints.curricula = 'Select at least one curriculum before assigning subjects.';
  }

  if (lowered.includes('not available for the selected curriculum and grade')) {
    hints.subjectIds = 'Selected subjects are not available for the selected curriculum and grade.';
  }

  if (lowered.includes('invalid active portal for this tutor')) {
    hints.activePortal = message;
  }

  if (lowered.includes('only admins can change') && lowered.includes('portal access')) {
    hints.portals = message;
  }

  if (lowered.includes('country does not allow one or more curricula')) {
    if (!hints.country) {
      hints.country = 'Country and curricula selection do not match.';
    }
    if (!hints.curricula) {
      hints.curricula = message;
    }
  }

  if (lowered.includes('caps is only available')) {
    hints.curricula = 'CAPS is only available for South African users.';
    hints.country = 'Select country as South Africa to use CAPS.';
  }

  const userFriendlyMessage = requirementMessages.length
    ? `${message} ${requirementMessages.length ? `(${requirementMessages.join(' ')})` : ''}`.trim()
    : message;

  return {
    feedbackMessage: userFriendlyMessage,
    hints,
  };
};

const getValidationFeedbackMessage = (errors, role) => {
  const fieldLabels = {
    name: 'enter the student name',
    surname: 'enter the student surname',
    email: 'enter a valid email address',
    password: 'enter a password of at least 8 characters',
    confirmPassword: 'make sure the passwords match',
    grade: 'select a valid grade',
    subjectIds: 'select at least one subject',
    tutorIds: 'select at least one tutor',
    tutoringOptions: 'select at least one delivery method',
    curricula: 'fix the curricula selection',
    country: 'fix the country selection',
    portals: 'select at least one tutor portal',
    activePortal: 'select a valid active portal',
  };
  const firstKey = Object.keys(errors || {})[0];
  const action = fieldLabels[firstKey];
  const roleLabel = cleanText(role) || 'user';

  if (!firstKey || !action) {
    return 'Please fix the highlighted fields before saving.';
  }

  return `Cannot save ${roleLabel}: ${action}.`;
};

const getLegacyStudentProfileWarnings = (formState = {}) => {
  const warnings = [];
  if (!combinePhoneValue(formState.phoneCountryCode, formState.phoneNationalNumber)) {
    warnings.push('phone number');
  }
  if (!Array.isArray(formState.tutoringOptions) || !formState.tutoringOptions.length) {
    warnings.push('delivery method');
  }
  const needsLocalAddress =
    cleanText(formState.country).toLowerCase() === 'south africa' &&
    Array.isArray(formState.tutoringOptions) &&
    formState.tutoringOptions.includes('in-person');
  if (needsLocalAddress && !cleanText(formState.province)) warnings.push('province');
  if (needsLocalAddress && !cleanText(formState.postalCode)) warnings.push('postal code');
  return warnings;
};

const getAllowedCurriculaForCountry = (country) =>
  cleanText(country).toLowerCase() === 'south africa'
    ? curriculumOptions
    : curriculumOptions.filter((entry) => !['CAPS', 'IEB'].includes(entry));

const getSubjectFilterContext = (formState) => ({
  role: formState.role,
  curricula: splitListInput(formState.curricula),
  grade: formState.grade,
  gradeLevels: splitListInput(formState.gradeLevels),
});

const normalizeTutorPortals = (value) => {
  const base = Array.isArray(value) ? value : value ? [value] : [];
  const allowed = new Set(tutorPortalOptions.map((option) => option.value));
  const out = [];
  base.forEach((entry) => {
    const normalized = String(entry || '').trim().toLowerCase();
    if (!allowed.has(normalized) || out.includes(normalized)) return;
    out.push(normalized);
  });
  if (!out.includes('tutor')) out.unshift('tutor');
  return out;
};

const pickFirstPortal = (portals, fallback = 'tutor') => {
  const normalized = normalizeTutorPortals(portals);
  if (!normalized.length) return 'tutor';
  if (normalized.includes(fallback)) return fallback;
  return normalized[0];
};

const requiresStudentDeliveryMethod = (program) => String(program || 'general').toLowerCase() !== 'math';
const requiresTutorDeliveryMethod = (portals) =>
  normalizeTutorPortals(portals).some((portal) => portal === 'tutor' || portal === 'matric');

const formatLocation = (user) => {
  const parts = [user?.suburb, user?.town, user?.province, user?.country]
    .map(cleanText)
    .filter(Boolean);
  if (!parts.length) return '—';
  return uniqueItems(parts).join(', ');
};

const defaultFormState = {
  name: '',
  surname: '',
  email: '',
  password: '',
  confirmPassword: '',
  role: 'student',
  program: 'general',
  grade: '',
  tutorIds: [],
  subjectIds: [],
  phone: '',
  phoneCountryCode: '+27',
  phoneNationalNumber: '',
  country: 'South Africa',
  identityNumber: '',
  dateOfBirth: '',
  gender: '',
  addressLine1: '',
  province: '',
  areaCode: '',
  postalCode: '',
  postalCodes: '',
  town: '',
  suburb: '',
  bio: '',
  degreeType: '',
  degreeSpecialization: '',
  institutionName: '',
  graduationYear: '',
  degrees: [],
  subjectExperience: [],
  teachingStyles: [],
  languagesSpoken: { southAfrican: [], international: [] },
  availabilitySchedule: emptyAvailability(),
  internetDeviceReadiness: emptyReadiness(),
  specialNeedsSupport: [],
  hasPoliceClearance: false,
  hasSaceRegistration: false,
  saceRegistrationNumber: '',
  bankName: '',
  bankAccountName: '',
  bankAccountNumber: '',
  paypalAccountEmail: '',
  curricula: [],
  gradeLevels: '',
  subjectFocus: '',
  tutoringOptions: [],
  schoolName: '',
  preferredTutorGender: '',
  preferredTeachingStyles: [],
  preferredLanguage: '',
  learningSupportNeeds: [],
  learningStyle: '',
  academicGoals: '',
  studentNotes: '',
  preferredLessonDuration: '',
  preferredLessonFrequency: '',
  accountAccessMode: 'manual',
  tutorOverrideReason: '',
  portals: ['tutor'],
  activePortal: 'tutor',
};

const defaultBookingStudentPatch = (parent = {}) => ({
  name: '',
  surname: '',
  schoolName: '',
  grade: '',
  curriculum: '',
  curriculumLevel: '',
  curriculumSubLevel: '',
  country: parent?.country || 'South Africa',
  timeZone: (parent?.country || 'South Africa') === 'South Africa' ? 'Africa/Johannesburg' : 'UTC',
  province: '',
  town: '',
  suburb: '',
  addressLine1: '',
  postalCode: '',
  subjects: [],
  primarySubject: '',
  deliveryMode: (parent?.country || 'South Africa') === 'South Africa' ? '' : 'online',
  preferredLessonDuration: '1',
  preferredLessonFrequency: '',
  preferredLanguage: '',
  preferredTutorGender: '',
  preferredTeachingStyles: [],
  learningSupportNeeds: [],
  learningStyle: '',
  specialNeedsSupport: [],
  availabilitySchedule: {
    daysAvailable: [],
    timeZone: (parent?.country || 'South Africa') === 'South Africa' ? 'Africa/Johannesburg' : 'UTC',
    timeSlots: [],
  },
  academicGoals: '',
  notes: '',
});

const patchFromRecommendedStudent = (recommended = {}, parent = {}) => ({
  ...defaultBookingStudentPatch(parent),
  ...recommended,
  subjects: Array.isArray(recommended.subjects) ? recommended.subjects : [],
  preferredTeachingStyles: Array.isArray(recommended.preferredTeachingStyles) ? recommended.preferredTeachingStyles : [],
  learningSupportNeeds: Array.isArray(recommended.learningSupportNeeds) ? recommended.learningSupportNeeds : [],
  specialNeedsSupport: Array.isArray(recommended.specialNeedsSupport) ? recommended.specialNeedsSupport : [],
  availabilitySchedule: {
    ...defaultBookingStudentPatch(parent).availabilitySchedule,
    ...(recommended.availabilitySchedule || {}),
  },
});

const formatAdminMoney = (amount, currency = 'ZAR') => {
  const value = Number(amount || 0);
  const rounded = Number.isFinite(value) ? value : 0;
  if (String(currency).toUpperCase() === 'ZAR') return `R${Math.round(rounded).toLocaleString('en-ZA')}`;
  return `$${rounded.toFixed(2)}`;
};
const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const getDisplayName = (user) => {
  if (!user) return 'Unnamed';
  const parts = [cleanText(user.name), cleanText(user.surname)].filter(Boolean);
  return parts.length ? parts.join(' ') : 'Unnamed';
};

const StatusChip = ({ user = {} }) => {
  const role = cleanText(user.role).toLowerCase();
  const approval = cleanText(user.approvalStatus || user.status).toLowerCase();
  if (user.isLocked) return <Chip size="small" label="Locked" color="warning" variant="outlined" />;
  if (role === 'tutor' && approval === 'rejected') return <Chip size="small" label="Rejected" color="error" />;
  if (role === 'tutor' && approval === 'pending') return <Chip size="small" label="Pending" color="warning" variant="outlined" />;
  if (role === 'tutor' && approval === 'approved') return <Chip size="small" label="Approved" color="success" />;
  return <Chip size="small" label="Active" color="success" />;
};

const getTutorProgramLabel = (tutor = {}) => {
  const raw = Array.isArray(tutor.program) ? tutor.program[0] : tutor.program;
  const normalized = cleanText(raw || tutor.activePortal || 'general').toLowerCase();
  if (normalized === 'math') return 'Math';
  if (normalized === 'matric') return 'Matric';
  return 'General';
};

const getTutorStatusLabel = (tutor = {}) => {
  if (tutor.isLocked) return 'Locked';
  const approval = cleanText(tutor.approvalStatus || tutor.status).toLowerCase();
  if (approval === 'rejected') return 'Rejected';
  if (approval === 'pending') return 'Pending';
  if (approval === 'approved') return 'Approved';
  return 'Unclassified';
};

const isAssignableTutor = (tutor = {}) =>
  (tutor.role || '').toLowerCase() === 'tutor' &&
  tutor.isLocked !== true &&
  ['approved', 'active', ''].includes(cleanText(tutor.approvalStatus || tutor.status).toLowerCase());

const getTutorSearchText = (tutor = {}, subjectsCatalog = []) =>
  [
    getDisplayName(tutor),
    tutor.email,
    getTutorProgramLabel(tutor),
    getTutorStatusLabel(tutor),
    ...getTutorSubjectLabels(tutor, subjectsCatalog),
    ...getTutorCurricula(tutor),
    ...getTutorGrades(tutor),
    ...getTutorDeliveryMethods(tutor),
    ...getTutorPostalCodes(tutor),
    tutor.province,
    tutor.town,
    tutor.suburb,
    tutor.areaCode,
    tutor.country,
  ]
    .map((item) => cleanText(String(item || '')).toLowerCase())
    .filter(Boolean)
    .join(' ');

const TutorOptionDetails = ({ tutor, subjectsCatalog = [], compact = false }) => {
  const subjectText = formatFilterList(getTutorSubjectLabels(tutor, subjectsCatalog), 'Subjects not listed', compact ? 2 : 4);
  const curriculumText = formatFilterList(getTutorCurricula(tutor), 'Curriculum not listed', compact ? 2 : 3);
  const gradeText = formatFilterList(getTutorGrades(tutor), 'Grades not listed', compact ? 3 : 5);
  const deliveryText = formatFilterList(getTutorDeliveryMethods(tutor), 'Delivery not listed', compact ? 2 : 3);
  const locationText = formatLocation(tutor);
  const postalText = formatFilterList(getTutorPostalCodes(tutor), 'Postal code not listed', compact ? 2 : 3);
  const status = getTutorStatusLabel(tutor);
  const isUnavailable = tutor?.isLocked || !['', 'active', 'approved'].includes(status.toLowerCase());

  return (
    <Stack spacing={0.75} sx={{ width: '100%' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
        <Typography variant="body2" sx={{ fontWeight: 800 }}>
          {getDisplayName(tutor)}
        </Typography>
        <Chip size="small" label={getTutorProgramLabel(tutor)} color="primary" variant="outlined" />
        <Chip
          size="small"
          label={status}
          color={isUnavailable ? 'warning' : 'success'}
          variant={isUnavailable ? 'outlined' : 'filled'}
        />
      </Stack>
      {!compact && tutor?.email ? (
        <Typography variant="caption" color="text.secondary">
          {tutor.email}
        </Typography>
      ) : null}
      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
        <Chip size="small" label={`Subjects: ${subjectText}`} />
        <Chip size="small" label={`Curricula: ${curriculumText}`} />
        <Chip size="small" label={`Grades: ${gradeText}`} />
        <Chip size="small" label={`Delivery: ${deliveryText}`} />
        <Chip size="small" label={`Location: ${locationText}`} />
        <Chip size="small" label={`Postal: ${postalText}`} />
      </Stack>
    </Stack>
  );
};

const TutorDetailsAutocomplete = ({
  label = 'Search and allocate tutors',
  value = [],
  options = [],
  allTutors = [],
  subjectsCatalog = [],
  error = false,
  helperText = '',
  onChange,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const selectedIds = useMemo(() => (Array.isArray(value) ? value.map((id) => String(id)) : []), [value]);
  const mergedOptions = useMemo(() => {
    const map = new Map();
    options.forEach((tutor) => {
      if (tutor?._id) map.set(String(tutor._id), tutor);
    });
    selectedIds.forEach((id) => {
      const selectedTutor = allTutors.find((tutor) => String(tutor?._id) === id);
      if (selectedTutor?._id) map.set(String(selectedTutor._id), selectedTutor);
    });
    return Array.from(map.values());
  }, [options, allTutors, selectedIds]);
  const selectedTutors = selectedIds
    .map((id) => mergedOptions.find((tutor) => String(tutor._id) === id))
    .filter(Boolean);
  const openSearchResults = useCallback((event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    inputRef.current?.focus?.();
    setOpen(true);
  }, []);

  return (
    <Stack spacing={1} sx={{ width: '100%', minWidth: 0 }}>
      <Autocomplete
        multiple
        fullWidth
        openOnFocus
        autoHighlight
        clearOnBlur={false}
        disableCloseOnSelect
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        inputValue={inputValue}
        onInputChange={(event, nextValue) => {
          setInputValue(nextValue);
          setOpen(true);
        }}
        options={mergedOptions}
        value={selectedTutors}
        getOptionLabel={(option) => getDisplayName(option)}
        isOptionEqualToValue={(option, selected) => String(option?._id) === String(selected?._id)}
        filterOptions={(items, state) => {
          const query = cleanText(state.inputValue).toLowerCase();
          if (!query) return items;
          return items.filter((tutor) => getTutorSearchText(tutor, subjectsCatalog).includes(query));
        }}
        onChange={(event, selected) => {
          onChange((selected || []).map((tutor) => tutor._id).filter(Boolean));
        }}
        renderTags={(selected, getTagProps) =>
          selected.map((tutor, index) => (
            <Chip
              {...getTagProps({ index })}
              key={tutor._id}
              label={`${getDisplayName(tutor)} · ${getTutorProgramLabel(tutor)}`}
              size="small"
            />
          ))
        }
        renderOption={(props, option, { selected }) => (
          <Box component="li" {...props} key={option._id} sx={{ alignItems: 'flex-start !important', gap: 1.25, py: 1.25 }}>
            <Checkbox checked={selected} sx={{ mt: 0.25 }} />
            <TutorOptionDetails tutor={option} subjectsCatalog={subjectsCatalog} />
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder="Type a tutor name, subject, curriculum, grade, or area"
            error={error}
            helperText={helperText || 'Results update as you type. Select one or more approved tutors.'}
            inputRef={inputRef}
            onFocus={() => setOpen(true)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') openSearchResults(event);
            }}
          />
        )}
        ListboxProps={{ sx: { maxHeight: 420 } }}
        sx={{
          minWidth: 0,
          '& .MuiAutocomplete-inputRoot': { minHeight: 56, alignItems: 'center' },
          '& .MuiAutocomplete-input': { minWidth: '180px !important', flexGrow: 1 },
        }}
        noOptionsText="No approved tutors match this search and the learner requirements"
      />
      <Button
        variant="outlined"
        startIcon={<SearchRoundedIcon />}
        onMouseDown={openSearchResults}
        onClick={openSearchResults}
        sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' }, minHeight: 44 }}
      >
        Search tutors
      </Button>
    </Stack>
  );
};

const EmptyOverlay = ({ message }) => (
  <Stack height="100%" alignItems="center" justifyContent="center" spacing={1} sx={{ py: 4 }}>
    <Typography variant="subtitle1" color="text.secondary">
      {message}
    </Typography>
  </Stack>
);

const TutorFilterMultiSelect = ({ label, value, options, onChange }) => (
  <Box className="users-tutor-filter-field">
    <Typography component="label" className="users-tutor-filter-label">
      {label}
    </Typography>
    <FormControl fullWidth>
      <Select
        multiple
        displayEmpty
        value={value}
        input={<OutlinedInput />}
        onChange={(event) => onChange(event.target.value)}
        inputProps={{ 'aria-label': label }}
        renderValue={(selected) => (
          selected.length ? (
            <Box className="users-tutor-filter-values">
              {selected.map((item) => (
                <Chip key={item} label={item} size="small" />
              ))}
            </Box>
          ) : (
            <Typography component="span" className="users-tutor-filter-placeholder">
              All {label.toLowerCase()}
            </Typography>
          )
        )}
        MenuProps={{ PaperProps: { sx: { maxHeight: 360, minWidth: 240 } } }}
      >
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox checked={value.includes(option)} />
            <ListItemText primary={option} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Box>
);

const TutorFilterTextField = ({ label, value, placeholder, onChange }) => (
  <Box className="users-tutor-filter-field">
    <Typography component="label" className="users-tutor-filter-label">
      {label}
    </Typography>
    <TextField
      value={value}
      onChange={onChange}
      fullWidth
      placeholder={placeholder}
      inputProps={{ 'aria-label': label }}
    />
  </Box>
);

const Users = () => {
  const { user: currentUser, role: currentRole } = useContext(AuthContext) || {};
  const activeAdminRole = String(currentRole || currentUser?.role || '').toLowerCase();
  const studentV2Available = process.env.REACT_APP_ADMIN_STUDENT_V2 === 'true' &&
    String(process.env.REACT_APP_ADMIN_STUDENT_V2_ROLES || 'superadmin')
      .split(',')
      .map((role) => role.trim().toLowerCase())
      .includes(activeAdminRole);
  const studentActivationAvailable = studentV2Available &&
    process.env.REACT_APP_ADMIN_STUDENT_ACTIVATION_ENABLED === 'true' &&
    String(process.env.REACT_APP_ADMIN_STUDENT_ACTIVATION_ROLES || 'superadmin')
      .split(',')
      .map((role) => role.trim().toLowerCase())
      .includes(activeAdminRole);

  const [users, setUsers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('students');
  const [tutorFilters, setTutorFilters] = useState(defaultTutorFilters);
  const [tutorSearchRows, setTutorSearchRows] = useState(null);
  const [tutorSearchLoading, setTutorSearchLoading] = useState(false);
  const [tutorSearchError, setTutorSearchError] = useState('');
  const [importSummary, setImportSummary] = useState(null);

  const [formState, setFormState] = useState(defaultFormState);
  const [formErrors, setFormErrors] = useState({});
  const [formDialog, setFormDialog] = useState({ open: false, mode: 'create', loading: false });
  const [roleDialog, setRoleDialog] = useState({ open: false, role: '' });
  const [studentStep, setStudentStep] = useState(0);
  const [studentEditSummary, setStudentEditSummary] = useState(false);
  const [studentTutorEligibility, setStudentTutorEligibility] = useState([]);
  const [studentTutorEligibilityLoading, setStudentTutorEligibilityLoading] = useState(false);
  const [studentTutorEligibilityError, setStudentTutorEligibilityError] = useState('');
  const studentCreateKeyRef = useRef('');
  const [tutorStep, setTutorStep] = useState(0);
  const [tutorEditSummary, setTutorEditSummary] = useState(false);
  const [viewDialog, setViewDialog] = useState({ open: false, user: null });
  const [passwordDialog, setPasswordDialog] = useState({ open: false, user: null, loading: false, newPassword: '', confirmPassword: '' });
  const [resendDialog, setResendDialog] = useState({ open: false, user: null, loading: false, password: '' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', description: '', loading: false, onConfirm: null });
  const [toolbarMenuAnchor, setToolbarMenuAnchor] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuUser, setMenuUser] = useState(null);
  const [portalRequirements, setPortalRequirements] = useState({});
  const [portalRequirementDialog, setPortalRequirementDialog] = useState({ open: false, requirements: [] });
  const [bookingParentDialog, setBookingParentDialog] = useState({
    open: false,
    mode: 'create',
    loading: false,
    user: null,
    name: '',
    surname: '',
    email: '',
    phone: '',
    country: 'South Africa',
  });
  const [studentLinkDialog, setStudentLinkDialog] = useState({
    open: false,
    mode: 'link',
    parent: null,
    query: '',
    loading: false,
    linking: false,
    rows: [],
    selected: null,
    profilePatch: {},
    savedStudent: null,
    packages: [],
    packageName: '',
    packageLoading: false,
    packageAssigning: false,
    packageBooking: null,
    paymentProvider: 'yoco',
    paymentLinkCreating: false,
    paymentLink: '',
    forceReassign: false,
    reason: '',
    error: '',
  });

  const fileInputRef = useRef(null);

  const effectiveRole = useMemo(() => {
    if (currentRole) return currentRole;
    if (currentUser?.role) return currentUser.role;
    try {
      const storedRole = localStorage.getItem('role');
      if (storedRole) return storedRole;
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        if (parsed?.role) return parsed.role;
      }
    } catch {
      /* ignore */
    }
    return null;
  }, [currentRole, currentUser?.role]);

  const isSuperAdmin = useMemo(() => {
    const normalized = (effectiveRole || '').toLowerCase().replace(/[^a-z]/g, '');
    return normalized === 'superadmin';
  }, [effectiveRole]);
  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [fetchedUsers, subjectResp, requirementResp, matricSummaryResp] = await Promise.all([
        fetchUsers(),
        fetchSubjects({ active: true }),
        fetchPortalRequirements().catch(() => ({})),
        api.get('/matric-rewrite/admin/learner-summaries').catch(() => null),
      ]);
      const matricSummaries = new Map(
        (matricSummaryResp?.data?.items || []).map((item) => [String(item.lmsStudentId), item]),
      );
      setUsers(fetchedUsers.map((user) => (
        String(user?.program || '').toLowerCase() === 'matric'
          ? { ...user, matricEnrollment: matricSummaries.get(String(user._id)) || null }
          : user
      )));
      setSubjects(subjectResp.items || []);
      setPortalRequirements(requirementResp || {});
    } catch (err) {
      setError(err?.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const applyUserSearch = useCallback(() => {
    setSearchTerm(cleanText(searchInput));
  }, [searchInput]);

  const clearUserSearch = useCallback(() => {
    setSearchInput('');
    setSearchTerm('');
  }, []);

  const searchValue = searchTerm.trim().toLowerCase();
  const filteredUsers = useMemo(() => {
    if (!searchValue) return users;
    return users.filter((user) => {
      const name = cleanText(user.name).toLowerCase();
      const surname = cleanText(user.surname).toLowerCase();
      const email = cleanText(user.email).toLowerCase();
      return name.includes(searchValue) || surname.includes(searchValue) || email.includes(searchValue) || cleanText(user.adminReference).toLowerCase().includes(searchValue) || String(user._id).toLowerCase().includes(searchValue);
    });
  }, [users, searchValue]);

  const getProgram = useCallback((user) => (user?.program || 'general').toLowerCase(), []);
  const hasTutorPortal = useCallback((user, portal) => {
    const role = (user?.role || '').toLowerCase();
    if (role !== 'tutor') return false;
    const portals = normalizeTutorPortals(user?.portals || []);
    return portals.includes(String(portal || '').toLowerCase());
  }, []);
  const groupedUsers = useMemo(() => {
    const groups = {
      students: [],
      tutors: [],
      parents: [],
      admins: [],
      mathStudents: [],
      matricStudents: [],
      mathTutors: [],
      matricTutors: [],
      all: filteredUsers,
    };

    filteredUsers.forEach((user) => {
      const program = getProgram(user);
      const role = (user?.role || '').toLowerCase();

      if (role === 'student') {
        if (program === 'math') groups.mathStudents.push(user);
        else if (program === 'matric') groups.matricStudents.push(user);
        else groups.students.push(user);
      } else if (role === 'tutor') {
        const inGeneralPortal = hasTutorPortal(user, 'tutor');
        const inMathPortal = hasTutorPortal(user, 'math');
        const inMatricPortal = hasTutorPortal(user, 'matric');
        if (inGeneralPortal) groups.tutors.push(user);
        if (inMathPortal) groups.mathTutors.push(user);
        if (inMatricPortal) groups.matricTutors.push(user);
      } else if (role === 'admin' || role === 'superadmin') {
        groups.admins.push(user);
      } else if (role === 'parent') {
        groups.parents.push(user);
      }
    });

    return groups;
  }, [filteredUsers, getProgram, hasTutorPortal]);

  const tutorFilterOptions = useMemo(() => {
    const options = {
      curricula: new Set(),
      subjects: new Set(),
      grades: new Set(),
      postalCodes: new Set(),
      deliveryMethods: new Set(),
      provinces: new Set(),
      suburbs: new Set(),
      towns: new Set(),
      areaCodes: new Set(),
      countries: new Set(),
      statuses: new Set(['Approved', 'Pending', 'Rejected', 'Unclassified', 'Locked']),
    };

    groupedUsers.tutors.forEach((tutor) => {
      getTutorCurricula(tutor).forEach((entry) => options.curricula.add(entry));
      getTutorSubjectLabels(tutor, subjects).forEach((entry) => options.subjects.add(entry));
      getTutorGrades(tutor).forEach((entry) => options.grades.add(entry));
      getTutorPostalCodes(tutor).forEach((entry) => options.postalCodes.add(entry));
      getTutorDeliveryMethods(tutor).forEach((entry) => options.deliveryMethods.add(entry));
      if (cleanText(tutor.province)) options.provinces.add(cleanText(tutor.province));
      if (cleanText(tutor.suburb)) options.suburbs.add(cleanText(tutor.suburb));
      if (cleanText(tutor.town)) options.towns.add(cleanText(tutor.town));
      if (cleanText(tutor.areaCode)) options.areaCodes.add(cleanText(tutor.areaCode));
      if (cleanText(tutor.country)) options.countries.add(cleanText(tutor.country));
    });

    const sortValues = (set) => Array.from(set).sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }));
    return {
      curricula: sortValues(options.curricula),
      subjects: sortValues(options.subjects),
      grades: sortValues(options.grades),
      postalCodes: sortValues(options.postalCodes),
      deliveryMethods: sortValues(options.deliveryMethods),
      provinces: sortValues(options.provinces),
      suburbs: sortValues(options.suburbs),
      towns: sortValues(options.towns),
      areaCodes: sortValues(options.areaCodes),
      countries: sortValues(options.countries),
      statuses: sortValues(options.statuses),
    };
  }, [groupedUsers.tutors, subjects]);

  const activeTutorFilterCount = useMemo(
    () =>
      tutorFilters.curricula.length +
      tutorFilters.subjects.length +
      tutorFilters.grades.length +
      tutorFilters.deliveryMethods.length +
      tutorFilters.provinces.length +
      tutorFilters.suburbs.length +
      tutorFilters.towns.length +
      tutorFilters.areaCodes.length +
      tutorFilters.countries.length +
      tutorFilters.statuses.length +
      tutorFilters.postalCodes.length +
      (cleanText(tutorFilters.postalCode) ? 1 : 0) +
      (cleanText(tutorFilters.area) ? 1 : 0),
    [tutorFilters],
  );

  const updateTutorFilter = useCallback((key, value) => {
    setTutorFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const buildTutorSearchParams = useCallback((filters) => {
    const params = { role: 'tutor', portal: 'tutor' };
    if (filters.curricula.length) params.curriculum = filters.curricula;
    if (filters.subjects.length) params.subject = filters.subjects;
    if (filters.grades.length) params.grade = filters.grades;
    if (filters.deliveryMethods.length) params.deliveryMethod = filters.deliveryMethods;
    if (filters.provinces.length) params.province = filters.provinces;
    if (filters.suburbs.length) params.suburb = filters.suburbs;
    if (filters.towns.length) params.town = filters.towns;
    if (filters.areaCodes.length) params.areaCode = filters.areaCodes;
    if (filters.countries.length) params.country = filters.countries;
    if (filters.statuses.length) params.status = filters.statuses;
    if (filters.postalCodes.length) params.postalCodes = filters.postalCodes;
    if (cleanText(filters.postalCode)) params.postalCode = cleanText(filters.postalCode);
    if (cleanText(filters.area)) params.area = cleanText(filters.area);
    return params;
  }, []);

  const searchTutors = useCallback(async () => {
    setTutorSearchLoading(true);
    setTutorSearchError('');
    try {
      const rows = await fetchUsers(buildTutorSearchParams(tutorFilters));
      setTutorSearchRows(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setTutorSearchError(err?.message || 'Failed to search tutors.');
    } finally {
      setTutorSearchLoading(false);
    }
  }, [buildTutorSearchParams, tutorFilters]);

  const clearTutorFilters = useCallback(() => {
    setTutorFilters(defaultTutorFilters);
    setTutorSearchRows(null);
    setTutorSearchError('');
  }, []);

  const tabs = useMemo(
    () => [
      { key: 'students', label: `Students (${groupedUsers.students.length})`, rows: groupedUsers.students },
      { key: 'tutors', label: `Tutors (${groupedUsers.tutors.length})`, rows: groupedUsers.tutors },
      { key: 'parents', label: `Parents (${groupedUsers.parents.length})`, rows: groupedUsers.parents },
      { key: 'admins', label: `Admins (${groupedUsers.admins.length})`, rows: groupedUsers.admins },
      { key: 'mathStudents', label: `Math Students (${groupedUsers.mathStudents.length})`, rows: groupedUsers.mathStudents },
      { key: 'matricStudents', label: `Matric Students (${groupedUsers.matricStudents.length})`, rows: groupedUsers.matricStudents },
      { key: 'mathTutors', label: `Math Educators (${groupedUsers.mathTutors.length})`, rows: groupedUsers.mathTutors },
      { key: 'matricTutors', label: `Matric Educators (${groupedUsers.matricTutors.length})`, rows: groupedUsers.matricTutors },
      { key: 'all', label: `All Users (${groupedUsers.all.length})`, rows: groupedUsers.all },
    ],
    [groupedUsers],
  );

  useEffect(() => {
    if (!tabs.some((tab) => tab.key === activeTab) && tabs.length) {
      setActiveTab(tabs[0].key);
    }
  }, [activeTab, tabs]);

  const visibleUsers = useMemo(() => {
    const tab = tabs.find((item) => item.key === activeTab);
    let rows = tab ? tab.rows : [];
    if (activeTab === 'tutors' && Array.isArray(tutorSearchRows)) {
      rows = tutorSearchRows;
      if (searchValue) {
        rows = rows.filter((user) => {
          const name = cleanText(user.name).toLowerCase();
          const surname = cleanText(user.surname).toLowerCase();
          const email = cleanText(user.email).toLowerCase();
          return name.includes(searchValue) || surname.includes(searchValue) || email.includes(searchValue) || cleanText(user.adminReference).toLowerCase().includes(searchValue) || String(user._id).toLowerCase().includes(searchValue);
        });
      }
    }
    return rows;
  }, [activeTab, searchValue, tabs, tutorSearchRows]);

  const allTutors = useMemo(
    () => users.filter((u) => (u.role || '').toLowerCase() === 'tutor' && getProgram(u) !== 'matric'),
    [users, getProgram],
  );
  const assignableTutors = useMemo(() => allTutors.filter(isAssignableTutor), [allTutors]);
  const generalTutors = useMemo(() => assignableTutors.filter((u) => getProgram(u) === 'general'), [assignableTutors, getProgram]);
  const mathTutors = useMemo(() => assignableTutors.filter((u) => getProgram(u) === 'math'), [assignableTutors, getProgram]);

  const availableTutorOptions = useMemo(() => {
    if (formState.role !== 'student') return assignableTutors;
    const program = (formState.program || 'general').toLowerCase();
    if (program === 'math') return mathTutors;
    return generalTutors;
  }, [formState.program, formState.role, assignableTutors, generalTutors, mathTutors]);
  const curriculumFilteredSubjects = useMemo(
    () => filterSubjectsForCurriculum(subjects, getSubjectFilterContext(formState)),
    [subjects, formState],
  );
  const bookingStudentSubjectOptions = useMemo(
    () => filterSubjectsForCurriculum(subjects, {
      role: 'student',
      curricula: studentLinkDialog.profilePatch.curriculum ? [studentLinkDialog.profilePatch.curriculum] : [],
      grade: studentLinkDialog.profilePatch.grade,
      gradeLevels: [],
    }),
    [subjects, studentLinkDialog.profilePatch.curriculum, studentLinkDialog.profilePatch.grade],
  );
  const hasCurriculumSubjectContext = splitListInput(formState.curricula).length > 0 && cleanText(formState.grade);
  const isStudentCreate = formDialog.mode === 'create' && formState.role === 'student';
  const isTutorCreate = formDialog.mode === 'create' && formState.role === 'tutor';
  const isAdminCreate =
    formDialog.mode === 'create' && (formState.role === 'admin' || formState.role === 'superadmin');
  const legacyStudentEditWarnings = useMemo(
    () => formDialog.mode === 'edit' && formState.role === 'student'
      ? getLegacyStudentProfileWarnings(formState)
      : [],
    [formDialog.mode, formState],
  );

  useEffect(() => {
    const allowedSubjectIds = new Set(curriculumFilteredSubjects.map((subject) => String(subject._id)));
    setFormState((prev) => {
      if (!Array.isArray(prev.subjectIds) || !prev.subjectIds.length) return prev;
      const nextSubjectIds = prev.subjectIds.filter((id) => allowedSubjectIds.has(String(id)));
      return nextSubjectIds.length === prev.subjectIds.length ? prev : { ...prev, subjectIds: nextSubjectIds };
    });
  }, [curriculumFilteredSubjects]);

  const resolvePortalRequirementsForTutor = useCallback(
    (portals) => {
      const normalizedPortals = normalizeTutorPortals(portals);
      const tutorRequirementConfig = portalRequirements?.tutor || {};
      return normalizedPortals
        .map((portal) => {
          const item = tutorRequirementConfig[portal];
          const requiredFields = Array.isArray(item?.requiredFields) ? item.requiredFields : [];
          return { portal, requiredFields };
        })
        .filter((item) => item.requiredFields.length > 0);
    },
    [portalRequirements],
  );

  const getMissingPortalRequirements = useCallback(() => {
    if ((formState.role || '').toLowerCase() !== 'tutor') return [];

    const requirements = resolvePortalRequirementsForTutor(formState.portals);
    const missing = [];
    requirements.forEach((item) => {
      item.requiredFields.forEach((field) => {
        if (field === 'tutoringOptions' && (!Array.isArray(formState.tutoringOptions) || !formState.tutoringOptions.length)) {
          missing.push(item);
        }
      });
    });
    return missing;
  }, [formState.portals, formState.role, formState.tutoringOptions, resolvePortalRequirementsForTutor]);

  const validateStudentStep = (step) => {
    const errors = {};
    const fieldsToClear = [];

    if (step === 0) {
      fieldsToClear.push('name', 'surname', 'email');
      if (!cleanText(formState.name)) errors.name = 'Name is required';
      if (!cleanText(formState.surname)) errors.surname = 'Surname is required';
      if (!cleanText(formState.email) || !/^\S+@\S+\.\S+$/.test(cleanText(formState.email))) {
        errors.email = 'Valid email is required';
      }
    }

    if (step === 1) {
      fieldsToClear.push('program', 'grade', 'subjectIds', 'curricula');
      if (!studentProgramOptions.some((option) => option.value === formState.program)) {
        errors.program = 'Select a student programme';
      }
      const gradeValue = parseStudentGrade(formState.grade);
      if (!Number.isInteger(gradeValue) || gradeValue < 0 || gradeValue > 12) {
        errors.grade = 'Grade is required for students';
      }
      if (formState.program === 'matric' && gradeValue !== 12) {
        errors.grade = 'Matric Programme is only available for Grade 12';
      }
      if (!splitListInput(formState.curricula).length) {
        errors.curricula = 'Select at least one curriculum';
      }
      if (requiresSouthAfricaForCaps(formState.country, formState.curricula)) {
        errors.curricula = 'CAPS is only available for South African users';
      }
      if (!formState.subjectIds.length) {
        errors.subjectIds = 'Select at least one subject';
      }
    }

    if (step === 2) {
      fieldsToClear.push('phone', 'country', 'tutoringOptions', 'province', 'postalCode');
      if (!combinePhoneValue(formState.phoneCountryCode, formState.phoneNationalNumber)) errors.phone = 'Cell number is required';
      if (!COUNTRY_OPTIONS.includes(formState.country)) errors.country = 'Select a valid country';
      if (!formState.tutoringOptions.length) errors.tutoringOptions = 'Select at least one delivery method';
      const localInPerson = formState.country === 'South Africa' && formState.tutoringOptions.includes('in-person');
      if (localInPerson && !cleanText(formState.province)) errors.province = 'Province is required for in-person tutoring';
      if (localInPerson && !cleanText(formState.postalCode)) errors.postalCode = 'Postal code is required for in-person tutoring';
    }

    if (step === 3 && formDialog.mode === 'create') {
      fieldsToClear.push('password', 'confirmPassword', 'tutorOverrideReason');
      const selectedTutorIds = new Set((formState.tutorIds || []).map(String));
      const hasAdvisoryMismatch = studentTutorEligibility.some((item) =>
        item.eligible && selectedTutorIds.has(String(item.tutor?._id)) && item.advisories?.length > 0
      );
      if (hasAdvisoryMismatch && !cleanText(formState.tutorOverrideReason)) {
        errors.tutorOverrideReason = 'Explain why the advisory mismatch is acceptable';
      }
      if (formState.accountAccessMode === 'manual') {
        if (!formState.password || formState.password.length < 8) {
          errors.password = 'Password must be at least 8 characters';
        }
        if (formState.password !== formState.confirmPassword) {
          errors.confirmPassword = 'Passwords do not match';
        }
      }
    }

    setFormErrors((prev) => {
      const next = { ...prev };
      fieldsToClear.forEach((field) => {
        delete next[field];
      });
      return { ...next, ...errors };
    });

    return Object.keys(errors).length === 0;
  };

  const handleStudentNext = () => {
    if (!validateStudentStep(studentStep)) return;
    setStudentStep((prev) => Math.min(prev + 1, studentStepLabels.length - 1));
    setStudentEditSummary(false);
  };

  const handleStudentBack = () => {
    setStudentStep((prev) => Math.max(prev - 1, 0));
    setStudentEditSummary(false);
  };

  const buildStudentCreatePayload = useCallback(() => ({
    name: cleanText(formState.name),
    surname: cleanText(formState.surname),
    email: cleanText(formState.email),
    program: formState.program,
    grade: parseStudentGrade(formState.grade),
    curricula: splitListInput(formState.curricula),
    subjectIds: Array.isArray(formState.subjectIds) ? formState.subjectIds : [],
    tutorIds: Array.isArray(formState.tutorIds) ? formState.tutorIds : [],
    phoneCountryCode: formState.phoneCountryCode,
    phoneNationalNumber: formState.phoneNationalNumber,
    phone: combinePhoneValue(formState.phoneCountryCode, formState.phoneNationalNumber),
    country: formState.country,
    province: cleanText(formState.province),
    town: cleanText(formState.town),
    suburb: cleanText(formState.suburb),
    postalCode: cleanText(formState.postalCode),
    areaCode: cleanText(formState.areaCode),
    addressLine1: cleanText(formState.addressLine1),
    schoolName: cleanText(formState.schoolName),
    tutoringOptions: Array.isArray(formState.tutoringOptions) ? formState.tutoringOptions : [],
    preferredTutorGender: cleanText(formState.preferredTutorGender),
    preferredTeachingStyles: formState.preferredTeachingStyles || [],
    preferredLanguage: cleanText(formState.preferredLanguage),
    learningSupportNeeds: formState.learningSupportNeeds || [],
    learningStyle: cleanText(formState.learningStyle),
    academicGoals: cleanText(formState.academicGoals),
    notes: cleanText(formState.studentNotes),
    specialNeedsSupport: formState.specialNeedsSupport || [],
    preferredLessonDuration: formState.preferredLessonDuration,
    preferredLessonFrequency: cleanText(formState.preferredLessonFrequency),
    availabilitySchedule: normalizeTutorEditorValue(formState.availabilitySchedule, emptyAvailability()),
    accountAccessMode: formState.accountAccessMode,
    tutorOverrideReason: cleanText(formState.tutorOverrideReason),
    password: formState.accountAccessMode === 'manual' ? formState.password : undefined,
  }), [formState]);

  const loadStudentTutorEligibility = useCallback(async () => {
    setStudentTutorEligibilityLoading(true);
    setStudentTutorEligibilityError('');
    try {
      const items = await fetchAdminStudentTutorEligibility(buildStudentCreatePayload());
      setStudentTutorEligibility(items);
      const eligibleIds = new Set(items.filter((item) => item.eligible).map((item) => String(item.tutor?._id)));
      setFormState((prev) => ({
        ...prev,
        tutorIds: prev.tutorIds.filter((id) => eligibleIds.has(String(id))),
      }));
    } catch (error) {
      setStudentTutorEligibilityError(error.message || 'Unable to load tutor matches.');
    } finally {
      setStudentTutorEligibilityLoading(false);
    }
  }, [buildStudentCreatePayload]);

  const validateTutorStep = (step) => {
    const errors = {};
    const fieldsToClear = [];

    if (step === 0) {
      fieldsToClear.push('name', 'surname', 'email');
      if (!cleanText(formState.name)) errors.name = 'Name is required';
      if (!cleanText(formState.surname)) errors.surname = 'Surname is required';
      if (!cleanText(formState.email) || !/^\S+@\S+\.\S+$/.test(cleanText(formState.email))) {
        errors.email = 'Valid email is required';
      }
    }

    if (step === 1) {
      fieldsToClear.push('grade', 'subjectIds', 'curricula', 'portals', 'activePortal');
      if (!formState.grade || Number.isNaN(Number(formState.grade))) {
        errors.grade = 'Grade is required';
      }
      if (!formState.subjectIds.length) {
        errors.subjectIds = 'Select at least one subject';
      }
      if (!splitListInput(formState.curricula).length) {
        errors.curricula = 'Curricula is required';
      }
      if (requiresSouthAfricaForCaps(formState.country, formState.curricula)) {
        errors.curricula = 'CAPS is only available for South African users';
      }
      const normalizedPortals = normalizeTutorPortals(formState.portals);
      if (!normalizedPortals.length) {
        errors.portals = 'Select at least one tutor portal';
      }
      const activePortal = cleanText(formState.activePortal).toLowerCase();
      if (!activePortal || !normalizedPortals.includes(activePortal)) {
        errors.activePortal = 'Select a valid active portal';
      }
    }

    if (step === 2) {
      fieldsToClear.push('country');
      if (requiresSouthAfricaForCaps(formState.country, formState.curricula)) {
        errors.country = 'CAPS is only available for South African users. Set country to South Africa.';
      }
    }

    if (step === 3 && formDialog.mode === 'create') {
      fieldsToClear.push('password', 'confirmPassword', 'tutoringOptions');
      if (!cleanText(formState.phone)) errors.phone = 'Cell number is required';
      if (!formState.password || formState.password.length < 8) {
        errors.password = 'Password must be at least 8 characters';
      }
      if (requiresTutorDeliveryMethod(formState.portals) && !formState.tutoringOptions.length) {
        errors.tutoringOptions = 'Select at least one delivery method';
      }
    }

    setFormErrors((prev) => {
      const next = { ...prev };
      fieldsToClear.forEach((field) => {
        delete next[field];
      });
      return { ...next, ...errors };
    });

    return Object.keys(errors).length === 0;
  };

  const handleTutorNext = () => {
    if (!validateTutorStep(tutorStep)) return;
    setTutorStep((prev) => Math.min(prev + 1, tutorStepLabels.length - 1));
    setTutorEditSummary(false);
  };

  const handleTutorBack = () => {
    setTutorStep((prev) => Math.max(prev - 1, 0));
    setTutorEditSummary(false);
  };

  const getTutorNames = useCallback(
    (tutorIds) => {
      if (!Array.isArray(tutorIds) || !tutorIds.length) return '—';
      return tutorIds
        .map((id) => {
          if (typeof id === 'object' && id !== null) {
            return getDisplayName(id);
          }
          const match = users.find((user) => String(user._id) === String(id));
          return match ? getDisplayName(match) : null;
        })
        .filter(Boolean)
        .join(', ');
    },
    [users],
  );

  const getSubjectNames = useCallback(
    (subjectIds) => {
      if (!Array.isArray(subjectIds) || !subjectIds.length) return '—';
      return subjectIds
        .map((id) => {
          if (typeof id === 'object' && id !== null) return id.name || null;
          const match = subjects.find((subject) => String(subject._id) === String(id));
          return match ? match.name : null;
        })
        .filter(Boolean)
        .join(', ');
    },
    [subjects],
  );

  const getSubjectNamesList = useCallback(
    (subjectIds) => {
      if (!Array.isArray(subjectIds) || !subjectIds.length) return [];
      return subjectIds
        .map((id) => {
          if (typeof id === 'object' && id !== null) return id.name || null;
          const match = subjects.find((subject) => String(subject._id) === String(id));
          return match ? match.name : null;
        })
        .filter(Boolean);
    },
    [subjects],
  );

  const openUserProfile = useCallback(async (user) => {
    if (!user) return;
    const isTutor = String(user.role || '').toLowerCase() === 'tutor';
    setViewDialog({
      open: true,
      user,
      applicationDocuments: null,
      applicationDocumentsLoading: isTutor,
      applicationDocumentsError: '',
    });
    if (!isTutor) return;

    try {
      const applicationDocuments = await fetchTutorApplicationDocuments(user._id);
      setViewDialog((current) => (
        String(current.user?._id) === String(user._id)
          ? { ...current, applicationDocuments, applicationDocumentsLoading: false }
          : current
      ));
    } catch (err) {
      setViewDialog((current) => (
        String(current.user?._id) === String(user._id)
          ? {
              ...current,
              applicationDocumentsLoading: false,
              applicationDocumentsError: err?.message || 'Failed to load tutor application documents.',
            }
          : current
      ));
    }
  }, []);

  const openTutorApplicationDocument = useCallback(async (documentSet, file) => {
    if (!file?.filename) return;
    try {
      const blob = documentSet?.source === 'current-profile'
        ? await downloadTutorProfileDocument(documentSet.userId, file.documentType, {
          degreeIndex: file.degreeIndex,
          documentIndex: file.documentIndex,
        })
        : await downloadTutorImportFile(documentSet?.importId, file.filename);
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (err) {
      setFeedback({ type: 'error', message: err?.message || 'Could not download tutor document.' });
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name & Email',
        flex: 1.4,
        minWidth: 220,
        sortable: true,
        renderCell: ({ row }) => (
          <Stack spacing={0.25}>
            <Typography variant="body1" fontWeight={600}>
              {getDisplayName(row)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {row.email || '—'}
              <br /><AdminReference record={row} />
            </Typography>
          </Stack>
        ),
        sortComparator: (v1, v2, cellParams1, cellParams2) =>
          getDisplayName(cellParams1.row).localeCompare(getDisplayName(cellParams2.row), undefined, {
            sensitivity: 'base',
            numeric: true,
          }),
      },
      {
        field: 'role',
        headerName: 'Role',
        width: 140,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            label={(row.role || 'unknown').replace(/^./, (c) => c.toUpperCase())}
            color={row.role === 'admin' || row.role === 'superadmin' ? 'info' : row.role === 'tutor' ? 'secondary' : row.role === 'parent' ? 'success' : 'primary'}
            variant="outlined"
          />
        ),
      },
      {
        field: 'program',
        headerName: 'Program',
        width: 150,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            label={(row.program || 'general').replace(/^./, (c) => c.toUpperCase())}
            variant="outlined"
            color="default"
          />
        ),
      },
      {
        field: 'details',
        headerName: 'Details',
        flex: 2.2,
        minWidth: 340,
        sortable: false,
        renderCell: ({ row }) => {
          const role = (row.role || '').toLowerCase();
          if (role === 'student') {
            const matricEnrollment = getProgram(row) === 'matric' ? row.matricEnrollment : null;
            return (
              <Stack spacing={0.25}>
                <Typography variant="body2">
                  <strong>Grade:</strong> {Number.isFinite(row.grade) ? row.grade : row.grade ?? '—'}
                </Typography>
                <Typography variant="body2">
                  <strong>Tutors:</strong> {getTutorNames(row.tutorIds)}
                </Typography>
                <Typography variant="body2">
                  <strong>Subjects:</strong> {getSubjectNames(row.subjectIds)}
                </Typography>
                <Typography variant="body2">
                  <strong>Account:</strong> {String(row.accountActivationStatus || 'not_required').replace(/_/g, ' ')}
                </Typography>
                {getProgram(row) === 'matric' && <Typography variant="body2">
                  <strong>Programme link:</strong> {matricEnrollment?.applicationReference || 'Not linked to a Matric enrolment'}
                  {matricEnrollment?.enrollmentStatus ? ` · ${String(matricEnrollment.enrollmentStatus).replace(/_/g, ' ')}` : ''}
                </Typography>}
              </Stack>
            );
          }
          if (role === 'tutor') {
            return (
              <Stack spacing={0.25}>
                <Typography variant="body2">
                  <strong>Subjects:</strong> {formatFilterList(getTutorSubjectLabels(row, subjects))}
                </Typography>
                <Typography variant="body2">
                  <strong>Curricula:</strong> {formatFilterList(getTutorCurricula(row))}
                </Typography>
                <Typography variant="body2">
                  <strong>Grades:</strong> {formatFilterList(getTutorGrades(row))}
                </Typography>
                <Typography variant="body2">
                  <strong>Postal:</strong> {formatFilterList(getTutorPostalCodes(row))}
                </Typography>
                <Typography variant="body2">
                  <strong>Delivery:</strong> {formatFilterList(getTutorDeliveryMethods(row))}
                </Typography>
                <Typography variant="body2">
                  <strong>Location:</strong> {formatLocation(row)}
                </Typography>
                <Typography variant="body2">
                  <strong>Phone:</strong> {cleanText(row.phone) || '—'}
                </Typography>
              </Stack>
            );
          }
          if (role === 'parent') {
            return (
              <Stack spacing={0.25}>
                <Typography variant="body2">
                  <strong>Source:</strong> Booking Parent
                </Typography>
                <Typography variant="body2">
                  <strong>Country:</strong> {cleanText(row.country) || '—'}
                </Typography>
                <Typography variant="body2">
                  <strong>Phone:</strong> {cleanText(row.phone) || '—'}
                </Typography>
                <Typography variant="body2">
                  <strong>Status:</strong> {String(row.role || '').toLowerCase() === 'tutor' ? getTutorStatusLabel(row) : (row.isLocked ? 'Inactive or suspended' : 'Active')}
                </Typography>
              </Stack>
            );
          }
          return (
            <Stack spacing={0.25}>
              <Typography variant="body2">
                <strong>Location:</strong> {formatLocation(row)}
              </Typography>
              <Typography variant="body2">
                <strong>Phone:</strong> {cleanText(row.phone) || '—'}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        sortable: false,
        renderCell: ({ row }) => <StatusChip user={row} />,
      },
      {
        field: 'actions',
        headerName: '',
        width: 96,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.25}>
            <IconButton
              size="small"
              aria-label="View profile"
              onClick={() => openUserProfile(row)}
            >
              <VisibilityRoundedIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label="More actions"
              onClick={(event) => {
                setMenuAnchor(event.currentTarget);
                setMenuUser(row);
              }}
            >
              <MoreVertRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [getSubjectNames, getTutorNames, openUserProfile, subjects],
  );
  const resetForm = useCallback(() => {
    setFormState(defaultFormState);
    setFormErrors({});
    setPortalRequirementDialog({ open: false, requirements: [] });
    setStudentStep(0);
    setStudentEditSummary(false);
    setStudentTutorEligibility([]);
    setStudentTutorEligibilityError('');
    studentCreateKeyRef.current = '';
    setTutorStep(0);
    setTutorEditSummary(false);
  }, []);

  const openCreateDialog = (role) => {
    if (!role) return;
    resetForm();
    setStudentStep(0);
    setStudentEditSummary(false);
    setTutorStep(0);
    setTutorEditSummary(false);
    setFormState((prev) => ({
      ...prev,
      role,
      program: role === 'student' ? 'general' : 'general',
      accountAccessMode: role === 'student' && studentActivationAvailable ? 'activation' : 'manual',
      portals: role === 'tutor' ? ['tutor'] : [],
      activePortal: role === 'tutor' ? 'tutor' : '',
    }));
    if (role === 'student') {
      studentCreateKeyRef.current = (typeof window !== 'undefined' && window.crypto?.randomUUID
        ? window.crypto.randomUUID().replace(/-/g, '')
        : `${Date.now()}${Math.random().toString(36).slice(2)}`);
    }
    setFormDialog({ open: true, mode: 'create', loading: false });
  };
  const openRoleDialog = () => {
    setRoleDialog({ open: true, role: '' });
  };

  const closeRoleDialog = () => {
    setRoleDialog({ open: false, role: '' });
  };

  const confirmRoleDialog = () => {
    if (!roleDialog.role) return;
    openCreateDialog(roleDialog.role);
    closeRoleDialog();
  };

  const openEditDialog = (user) => {
    if (!user) return;
    const phoneParts = splitPhoneValue(user.phone, user.country);
    const tutorIds = Array.isArray(user.tutorIds)
      ? user.tutorIds.map((t) => (typeof t === 'object' && t !== null ? t._id : t)).filter(Boolean)
      : [];
    const subjectIds = Array.isArray(user.subjectIds)
      ? user.subjectIds.map((s) => (typeof s === 'object' && s !== null ? s._id : s)).filter(Boolean)
      : [];
    const tutorSubjectNames = splitListInput(user.subjectFocus).map((name) => name.toLowerCase());
    const tutorSubjectIds =
      user.role === 'tutor'
        ? subjects
            .filter((subject) => tutorSubjectNames.includes(cleanText(subject.name).toLowerCase()))
            .map((subject) => subject._id)
        : [];
    const resolvedSubjectIds = user.role === 'tutor' && tutorSubjectIds.length ? tutorSubjectIds : subjectIds;
    const resolvedGrade =
      user.role === 'student'
        ? String(user.grade ?? '')
        : Array.isArray(user.gradeLevels) && user.gradeLevels.length
          ? String(user.gradeLevels[0])
          : '';
    setFormState({
      name: cleanText(user.name),
      surname: cleanText(user.surname),
      email: cleanText(user.email),
      password: '',
      confirmPassword: '',
      role: user.role || 'student',
      program: user.role === 'tutor' ? 'general' : (user.program || 'general'),
      grade: resolvedGrade,
      tutorIds,
      subjectIds: resolvedSubjectIds,
      phone: cleanText(user.phone),
      phoneCountryCode: phoneParts.phoneCountryCode,
      phoneNationalNumber: phoneParts.phoneNationalNumber,
      country: cleanText(user.country),
      identityNumber: cleanText(user.identityNumber),
      dateOfBirth: user.dateOfBirth ? String(user.dateOfBirth).slice(0, 10) : '',
      gender: cleanText(user.gender),
      addressLine1: cleanText(user.addressLine1),
      province: cleanText(user.province),
      areaCode: cleanText(user.areaCode),
      postalCode: cleanText(user.postalCode),
      postalCodes: Array.isArray(user.postalCodes) ? user.postalCodes.join(', ') : cleanText(user.postalCodes),
      town: cleanText(user.town),
      suburb: cleanText(user.suburb),
      bio: cleanText(user.bio),
      degreeType: cleanText(user.degreeType),
      degreeSpecialization: cleanText(user.degreeSpecialization),
      institutionName: cleanText(user.institutionName),
      graduationYear: user.graduationYear ? String(user.graduationYear) : '',
      degrees: normalizeTutorEditorValue(user.degrees, []),
      subjectExperience: normalizeTutorEditorValue(user.subjectExperience, []),
      teachingStyles: Array.isArray(user.teachingStyles) ? user.teachingStyles : splitListInput(user.teachingStyles),
      languagesSpoken: normalizeTutorEditorValue(user.languagesSpoken, { southAfrican: [], international: [] }),
      availabilitySchedule: normalizeTutorEditorValue(user.availabilitySchedule, emptyAvailability()),
      internetDeviceReadiness: normalizeTutorEditorValue(user.internetDeviceReadiness, emptyReadiness()),
      specialNeedsSupport: Array.isArray(user.specialNeedsSupport) ? user.specialNeedsSupport : splitListInput(user.specialNeedsSupport),
      learningSupportNeeds: Array.isArray(user.learningSupportNeeds) ? user.learningSupportNeeds : [],
      preferredTutorGender: cleanText(user.preferredTutorGender),
      preferredTeachingStyles: Array.isArray(user.preferredTeachingStyles) ? user.preferredTeachingStyles : [],
      preferredLanguage: cleanText(user.preferredLanguage),
      learningStyle: cleanText(user.learningStyle),
      academicGoals: cleanText(user.academicGoals),
      studentNotes: cleanText(user.studentNotes),
      preferredLessonDuration: user.preferredLessonDuration ? String(user.preferredLessonDuration) : '',
      preferredLessonFrequency: cleanText(user.preferredLessonFrequency),
      schoolName: cleanText(user.schoolName),
      accountAccessMode: 'manual',
      hasPoliceClearance: Boolean(user.hasPoliceClearance),
      hasSaceRegistration: Boolean(user.hasSaceRegistration),
      saceRegistrationNumber: cleanText(user.saceRegistrationNumber),
      bankName: cleanText(user.bankName),
      bankAccountName: cleanText(user.bankAccountName),
      bankAccountNumber: cleanText(user.bankAccountNumber),
      paypalAccountEmail: cleanText(user.paypalAccountEmail),
      curricula: Array.isArray(user.curricula) ? user.curricula : splitListInput(user.curricula),
      gradeLevels: Array.isArray(user.gradeLevels) ? user.gradeLevels.join(', ') : cleanText(user.gradeLevels),
      subjectFocus: Array.isArray(user.subjectFocus) ? user.subjectFocus.join(', ') : cleanText(user.subjectFocus),
      tutoringOptions: Array.isArray(user.tutoringOptions) ? user.tutoringOptions : [],
      portals: user.role === 'tutor' ? ['tutor'] : [],
      activePortal: user.role === 'tutor' ? 'tutor' : '',
    });
    setFormErrors({});
    setFormDialog({ open: true, mode: 'edit', loading: false, user });
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuUser(null);
  };

  const validateForm = () => {
    const errors = {};
    const role = (formState.role || '').toLowerCase();
    const isAdminRole = role === 'admin' || role === 'superadmin';
    if (!cleanText(formState.name)) errors.name = 'Name is required';
    if (!isAdminRole && !cleanText(formState.surname)) errors.surname = 'Surname is required';
    if (!cleanText(formState.email) || !/^\S+@\S+\.\S+$/.test(cleanText(formState.email))) {
      errors.email = 'Valid email is required';
    }
    if (isAdminRole && !cleanText(formState.phone)) errors.phone = 'Cell number is required';

    if (formDialog.mode === 'create') {
      const requiresManualPassword = formState.role !== 'student' || formState.accountAccessMode === 'manual';
      if (requiresManualPassword && (!formState.password || formState.password.length < 8)) {
        errors.password = 'Password must be at least 8 characters';
      }
      if (requiresManualPassword && !isAdminRole && formState.password !== formState.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }

    if (formState.role === 'student') {
      const requireCompleteStudentProfile = formDialog.mode === 'create';
      const gradeValue = parseStudentGrade(formState.grade);
      if (!Number.isInteger(gradeValue) || gradeValue < 0 || gradeValue > 12) {
        errors.grade = 'Grade is required for students';
      }
      if (!studentProgramOptions.some((option) => option.value === formState.program)) {
        errors.program = 'Select a student programme';
      }
      if (formState.program === 'matric' && gradeValue !== 12) {
        errors.grade = 'Matric Programme is only available for Grade 12';
      }
      if (!splitListInput(formState.curricula).length) {
        errors.curricula = 'Select at least one curriculum';
      }
      if (!formState.subjectIds.length) {
        errors.subjectIds = 'Select at least one subject';
      }
      if (requireCompleteStudentProfile && !combinePhoneValue(formState.phoneCountryCode, formState.phoneNationalNumber)) {
        errors.phone = 'Cell number is required';
      }
      if (requireCompleteStudentProfile && !formState.tutoringOptions.length) {
        errors.tutoringOptions = 'Select at least one delivery method';
      }
      if (requireCompleteStudentProfile && formState.country === 'South Africa' && formState.tutoringOptions.includes('in-person')) {
        if (!cleanText(formState.province)) errors.province = 'Province is required for in-person tutoring';
        if (!cleanText(formState.postalCode)) errors.postalCode = 'Postal code is required for in-person tutoring';
      }
    }

    if ((formState.role || '').toLowerCase() === 'tutor') {
      if (!splitListInput(formState.curricula).length) {
        errors.curricula = 'Select at least one curriculum';
      }
      const normalizedPortals = normalizeTutorPortals(formState.portals);
      if (!normalizedPortals.length) {
        errors.portals = 'Select at least one tutor portal';
      }
      const activePortal = cleanText(formState.activePortal).toLowerCase();
      if (!activePortal || !normalizedPortals.includes(activePortal)) {
        errors.activePortal = 'Select an active portal from the enabled portals';
      }
    }

    const selectedCurricula = splitListInput(formState.curricula);
    if (requiresSouthAfricaForCaps(formState.country, selectedCurricula)) {
      errors.curricula = 'CAPS is only available for South African users';
    }

    const requiresTutoringOptions =
      (role === 'student' && requiresStudentDeliveryMethod(formState.program)) ||
      (role === 'tutor' && requiresTutorDeliveryMethod(formState.portals));
    if (requiresTutoringOptions && !formState.tutoringOptions.length) {
      errors.tutoringOptions = 'Select at least one delivery method';
    }

    setFormErrors(errors);
    return errors;
  };

  const upsertUserLocally = (nextUser) => {
    if (!nextUser || !nextUser._id) return;
    setUsers((prev) => {
      const exists = prev.some((u) => String(u._id) === String(nextUser._id));
      if (exists) {
        return prev.map((u) => (String(u._id) === String(nextUser._id) ? { ...u, ...nextUser } : u));
      }
      return [nextUser, ...prev];
    });
  };

  const handleSubmitForm = async (skipRequirementPrompt = false) => {
    const hasCapsCountryConflict = requiresSouthAfricaForCaps(formState.country, formState.curricula);
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length) {
      setFeedback({
        type: 'error',
        message: hasCapsCountryConflict
          ? 'CAPS is only available for South African users. Set country to South Africa before saving.'
          : getValidationFeedbackMessage(validationErrors, formState.role),
      });
      return;
    }
    if (!skipRequirementPrompt) {
      const missingRequirements = getMissingPortalRequirements();
      if (missingRequirements.length) {
        setPortalRequirementDialog({ open: true, requirements: missingRequirements });
        return;
      }
    }
    setFormDialog((prev) => ({ ...prev, loading: true }));
    setFeedback(null);

    const tutorSubjectNames = formState.role === 'tutor' ? getSubjectNamesList(formState.subjectIds) : [];
    const gradeLevels =
      formState.role === 'tutor' && formState.grade ? [String(formState.grade)] : [];

    const role = (formState.role || '').toLowerCase();
    const isAdminAccount = role === 'admin' || role === 'superadmin';
    const adminNameParts = isAdminAccount && formDialog.mode === 'create'
      ? splitAdminDisplayName(formState.name)
      : null;

    const payload = {
      name: adminNameParts ? adminNameParts.name : cleanText(formState.name),
      surname: isAdminAccount
        ? (adminNameParts ? adminNameParts.surname : cleanText(formState.surname))
        : cleanText(formState.surname),
      email: cleanText(formState.email),
      role: formState.role,
      program: formState.role === 'tutor' ? 'general' : (formState.program || 'general'),
      phone:
        (formState.role || '').toLowerCase() === 'tutor'
          ? combinePhoneValue(formState.phoneCountryCode, formState.phoneNationalNumber)
          : cleanText(formState.phone),
      country: cleanText(formState.country),
      identityNumber: cleanText(formState.identityNumber),
      dateOfBirth: cleanText(formState.dateOfBirth),
      gender: cleanText(formState.gender),
      addressLine1: cleanText(formState.addressLine1),
      province: cleanText(formState.province),
      areaCode: cleanText(formState.areaCode),
      postalCode: cleanText(formState.postalCode),
      postalCodes: splitListInput(formState.postalCodes),
      town: cleanText(formState.town),
      suburb: cleanText(formState.suburb),
      bio: cleanText(formState.bio),
      degreeType: cleanText(formState.degreeType),
      degreeSpecialization: cleanText(formState.degreeSpecialization),
      institutionName: cleanText(formState.institutionName),
      graduationYear: cleanText(formState.graduationYear),
      degrees: normalizeTutorEditorValue(formState.degrees, []),
      subjectExperience: normalizeTutorEditorValue(formState.subjectExperience, []),
      teachingStyles: splitListInput(formState.teachingStyles),
      languagesSpoken: normalizeTutorEditorValue(formState.languagesSpoken, { southAfrican: [], international: [] }),
      availabilitySchedule: normalizeTutorEditorValue(formState.availabilitySchedule, emptyAvailability()),
      internetDeviceReadiness: normalizeTutorEditorValue(formState.internetDeviceReadiness, emptyReadiness()),
      specialNeedsSupport: splitListInput(formState.specialNeedsSupport),
      hasPoliceClearance: Boolean(formState.hasPoliceClearance),
      hasSaceRegistration: Boolean(formState.hasSaceRegistration),
      saceRegistrationNumber: cleanText(formState.saceRegistrationNumber),
      bankName: cleanText(formState.bankName),
      bankAccountName: cleanText(formState.bankAccountName),
      bankAccountNumber: cleanText(formState.bankAccountNumber),
      paypalAccountEmail: cleanText(formState.paypalAccountEmail),
      curricula: splitListInput(formState.curricula),
      gradeLevels,
      subjectFocus: tutorSubjectNames,
      tutoringOptions: Array.isArray(formState.tutoringOptions) ? formState.tutoringOptions : [],
    };

    if ((formState.role || '').toLowerCase() === 'tutor') {
      payload.generalTutorOnly = true;
      payload.program = 'general';
      payload.portals = ['tutor'];
      payload.activePortal = 'tutor';
    }

    if (formState.role === 'student') {
      const gradeValue = parseStudentGrade(formState.grade);
      if (Number.isInteger(gradeValue)) {
        payload.grade = gradeValue;
      }
      payload.tutorIds = Array.isArray(formState.tutorIds) ? formState.tutorIds : [];
      payload.subjectIds = Array.isArray(formState.subjectIds) ? formState.subjectIds : [];
    }

    try {
      if (formDialog.mode === 'create') {
        let finalUser;
        if (formState.role === 'student' && studentV2Available) {
          finalUser = await createAdminStudent(
            buildStudentCreatePayload(),
            studentCreateKeyRef.current || `${Date.now()}${Math.random().toString(36).slice(2)}`,
          );
        } else {
          const legacyPayload = formState.role === 'student'
            ? { ...payload, phone: combinePhoneValue(formState.phoneCountryCode, formState.phoneNationalNumber) }
            : payload;
          const created = await registerUser({ ...legacyPayload, password: formState.password });
          finalUser = formState.role === 'student' && formState.subjectIds.length
            ? await setUserSubjects(created._id, formState.subjectIds)
            : created;
        }
        upsertUserLocally(finalUser);
        setFeedback({
          type: 'success',
          message: formState.role === 'student'
            ? (studentV2Available
              ? 'LMS student created successfully with subjects and tutor assignments saved together.'
              : 'LMS student created using the legacy fallback workflow.')
            : 'User created successfully.',
        });
      } else if (formDialog.mode === 'edit' && formDialog.user) {
        const updated = await updateUser(formDialog.user._id, payload);
        let finalUser = updated;
        if (updated.role === 'student') {
          finalUser = await setUserSubjects(updated._id, formState.subjectIds || []);
        }
        upsertUserLocally(finalUser);
        setFeedback({ type: 'success', message: 'User updated successfully.' });
      }
      setFormDialog({ open: false, mode: 'create', loading: false });
      resetForm();
    } catch (err) {
      setFormDialog((prev) => ({ ...prev, loading: false }));
      const parsedError = mapBackendErrorToFieldHints(err);
      setFormErrors(parsedError?.hints || {});
      setFeedback({
        type: 'error',
        message: parsedError.feedbackMessage || err?.message || 'Failed to save user.',
      });
    }
  };

  const handlePortalRequirementContinue = () => {
    setPortalRequirementDialog({ open: false, requirements: [] });
    handleSubmitForm(true);
  };

  const handleDelete = (user) => {
    if (!user) return;
    setConfirmDialog({
      open: true,
      title: 'Delete user',
      description: `Are you sure you want to delete ${getDisplayName(user)}? This action cannot be undone.`,
      loading: false,
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          await deleteUser(user._id);
          setUsers((prev) => prev.filter((u) => String(u._id) !== String(user._id)));
          setFeedback({ type: 'success', message: 'User deleted successfully.' });
          setConfirmDialog({ open: false, title: '', description: '', loading: false, onConfirm: null });
        } catch (err) {
          setConfirmDialog({ open: false, title: '', description: '', loading: false, onConfirm: null });
          setFeedback({ type: 'error', message: err?.message || 'Failed to delete user.' });
        }
      },
    });
  };

  const copyTextToClipboard = async (text) => {
    if (!text) return false;
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    if (typeof window !== 'undefined') {
      window.prompt('Copy tutor profile link', text);
      return true;
    }
    return false;
  };

  const handleCreateTutorShareLink = async (user) => {
    if (!user?._id) return;
    try {
      const data = await createTutorProfileShareLink(user._id);
      await copyTextToClipboard(data.url);
      setUsers((prev) => prev.map((item) => (
        String(item._id) === String(user._id)
          ? { ...item, publicProfileEnabled: true, publicProfileToken: data.token }
          : item
      )));
      setFeedback({ type: 'success', message: 'Public tutor profile link copied.' });
    } catch (err) {
      setFeedback({ type: 'error', message: err?.message || 'Failed to create tutor profile share link.' });
    }
  };

  const handleDisableTutorShareLink = async (user) => {
    if (!user?._id) return;
    try {
      await disableTutorProfileShareLink(user._id);
      setUsers((prev) => prev.map((item) => (
        String(item._id) === String(user._id)
          ? { ...item, publicProfileEnabled: false }
          : item
      )));
      setFeedback({ type: 'success', message: 'Public tutor profile link disabled.' });
    } catch (err) {
      setFeedback({ type: 'error', message: err?.message || 'Failed to disable tutor profile share link.' });
    }
  };

  const handleResendStudentActivation = async (user) => {
    if (!user?._id) return;
    try {
      const result = await resendAdminStudentActivation(user._id);
      setUsers((prev) => prev.map((item) => String(item._id) === String(user._id)
        ? { ...item, accountActivationStatus: result.accountActivationStatus }
        : item));
      setFeedback({
        type: result.accountActivationStatus === 'delivery_failed' ? 'error' : 'success',
        message: result.message,
      });
    } catch (error) {
      setFeedback({ type: 'error', message: error.message || 'Unable to resend activation invitation.' });
    }
  };

  const openBookingParentDialog = () => {
    setBookingParentDialog({
      open: true,
      mode: 'create',
      loading: false,
      user: null,
      name: '',
      surname: '',
      email: '',
      phone: '',
      country: 'South Africa',
    });
  };

  const openEditBookingParentDialog = (user) => {
    setBookingParentDialog({
      open: true,
      mode: 'edit',
      loading: false,
      user,
      name: user?.name || '',
      surname: user?.surname || '',
      email: user?.email || '',
      phone: user?.phone || '',
      country: user?.country || 'South Africa',
    });
  };

  const handleCreateBookingParent = async () => {
    const payload = {
      name: cleanText(bookingParentDialog.name),
      surname: cleanText(bookingParentDialog.surname),
      email: cleanText(bookingParentDialog.email),
      phone: cleanText(bookingParentDialog.phone),
      country: cleanText(bookingParentDialog.country) || 'South Africa',
    };
    if (!payload.name || !payload.surname || !payload.email || !payload.phone) {
      setFeedback({ type: 'error', message: 'Name, surname, email, and phone are required for booking parents.' });
      return;
    }
    setBookingParentDialog((prev) => ({ ...prev, loading: true }));
    try {
      const editing = bookingParentDialog.mode === 'edit';
      const result = editing
        ? await updateAdminBookingParent(bookingParentDialog.user?.bookingUserId, payload)
        : await createAdminBookingParent(payload);
      await loadData();
      setBookingParentDialog((prev) => ({ ...prev, open: false, loading: false }));
      setFeedback({
        type: 'success',
        message: editing
          ? 'Booking parent updated successfully.'
          : result?.temporaryPassword
          ? `Booking parent created. Temporary password: ${result.temporaryPassword}`
          : 'Booking parent created or synced successfully.',
      });
    } catch (err) {
      setBookingParentDialog((prev) => ({ ...prev, loading: false }));
      setFeedback({ type: 'error', message: err?.message || 'Failed to create booking parent.' });
    }
  };

  const openStudentLinkDialog = async (parent) => {
    if (!parent?.bookingUserId) {
      setFeedback({ type: 'error', message: 'This parent account is not linked to a booking parent profile yet.' });
      return;
    }
    setStudentLinkDialog({
      open: true,
      mode: 'link',
      parent,
      query: '',
      loading: true,
      linking: false,
      rows: [],
      selected: null,
      profilePatch: defaultBookingStudentPatch(parent),
      savedStudent: null,
      packages: [],
      packageName: '',
      packageLoading: false,
      packageAssigning: false,
      packageBooking: null,
      paymentProvider: cleanText(parent.country).toLowerCase() === 'south africa' ? 'yoco' : 'paypal',
      paymentLinkCreating: false,
      paymentLink: '',
      forceReassign: false,
      reason: '',
      error: '',
    });
    try {
      const result = await fetchAdminLinkableLmsStudents({ linked: 'false', limit: 50 });
      setStudentLinkDialog((prev) => ({ ...prev, loading: false, rows: result.items || [] }));
    } catch (err) {
      setStudentLinkDialog((prev) => ({ ...prev, loading: false, error: err?.message || 'Failed to load LMS students.' }));
    }
  };

  const openManualBookingStudentDialog = (parent) => {
    if (!parent?.bookingUserId) {
      setFeedback({ type: 'error', message: 'This parent account is not linked to a booking parent profile yet.' });
      return;
    }
    setStudentLinkDialog({
      open: true,
      mode: 'create',
      parent,
      query: '',
      loading: false,
      linking: false,
      rows: [],
      selected: null,
      profilePatch: defaultBookingStudentPatch(parent),
      savedStudent: null,
      packages: [],
      packageName: '',
      packageLoading: false,
      packageAssigning: false,
      packageBooking: null,
      paymentProvider: cleanText(parent.country).toLowerCase() === 'south africa' ? 'yoco' : 'paypal',
      paymentLinkCreating: false,
      paymentLink: '',
      forceReassign: false,
      reason: '',
      error: '',
    });
  };

  const searchLinkableStudents = async (queryOverride) => {
    const query = queryOverride !== undefined ? queryOverride : studentLinkDialog.query;
    setStudentLinkDialog((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const result = await fetchAdminLinkableLmsStudents({
        q: cleanText(query),
        limit: 50,
      });
      setStudentLinkDialog((prev) => ({ ...prev, loading: false, rows: result.items || [] }));
    } catch (err) {
      setStudentLinkDialog((prev) => ({ ...prev, loading: false, error: err?.message || 'Failed to search LMS students.' }));
    }
  };

  const selectLinkStudent = (student) => {
    const recommended = student?.recommendedPayload || {};
    setStudentLinkDialog((prev) => ({
      ...prev,
      selected: student,
      profilePatch: patchFromRecommendedStudent(recommended, prev.parent),
      savedStudent: student.linkedBookingStudentId ? { _id: student.linkedBookingStudentId, ...recommended } : null,
      packages: [],
      packageName: '',
      packageBooking: null,
      paymentLink: '',
      forceReassign: student?.linkedStatus === 'linked',
    }));
  };

  const updateStudentLinkPatch = (key, value) => {
    setStudentLinkDialog((prev) => ({ ...prev, profilePatch: { ...prev.profilePatch, [key]: value } }));
  };

  const updateStudentAvailabilityPatch = (key, value) => {
    setStudentLinkDialog((prev) => ({
      ...prev,
      profilePatch: {
        ...prev.profilePatch,
        availabilitySchedule: {
          ...(prev.profilePatch.availabilitySchedule || {}),
          [key]: value,
        },
      },
    }));
  };

  const loadAdminStudentPackages = async (student) => {
    const studentId = student?._id;
    if (!studentId) return;
    setStudentLinkDialog((prev) => ({ ...prev, packageLoading: true, error: '', packages: [], packageName: '', packageBooking: null, paymentLink: '' }));
    try {
      const result = await fetchAdminBookingStudentPackages(studentId);
      setStudentLinkDialog((prev) => ({
        ...prev,
        packageLoading: false,
        packages: result.packages || [],
        packageName: result.packages?.[0]?.packageName || '',
      }));
    } catch (err) {
      setStudentLinkDialog((prev) => ({ ...prev, packageLoading: false, error: err?.message || 'Failed to load package options.' }));
    }
  };

  const handleLinkStudentToParent = async () => {
    const parentId = studentLinkDialog.parent?.bookingUserId;
    const selected = studentLinkDialog.selected;
    if (!parentId) return;
    if (studentLinkDialog.mode === 'link' && !selected?._id) return;
    setStudentLinkDialog((prev) => ({ ...prev, linking: true, error: '' }));
    try {
      const result = studentLinkDialog.mode === 'create'
        ? await createAdminBookingStudent(parentId, studentLinkDialog.profilePatch)
        : await linkAdminLmsStudentToBookingParent(parentId, {
            lmsUserId: selected._id,
            profilePatch: studentLinkDialog.profilePatch,
            forceReassign: studentLinkDialog.forceReassign,
            reason: studentLinkDialog.reason,
          });
      await loadData();
      setStudentLinkDialog((prev) => ({ ...prev, linking: false, savedStudent: result.student || null }));
      setFeedback({
        type: 'success',
        message: studentLinkDialog.mode === 'create'
          ? 'Booking student saved. Select a package and create a payment link.'
          : 'Existing LMS student linked. Select a package and create a payment link.',
      });
      await loadAdminStudentPackages(result.student);
    } catch (err) {
      setStudentLinkDialog((prev) => ({ ...prev, linking: false, error: err?.message || 'Failed to link student.' }));
    }
  };

  const handleAssignAdminPackage = async () => {
    const studentId = studentLinkDialog.savedStudent?._id;
    if (!studentId || !studentLinkDialog.packageName) return;
    setStudentLinkDialog((prev) => ({ ...prev, packageAssigning: true, error: '', packageBooking: null, paymentLink: '' }));
    try {
      const result = await assignAdminBookingStudentPackage(studentId, {
        packageName: studentLinkDialog.packageName,
        note: 'Admin assigned package from Users panel',
      });
      setStudentLinkDialog((prev) => ({
        ...prev,
        packageAssigning: false,
        packageBooking: result.booking || null,
        paymentProvider: (result.booking?.billingCurrency || '').toUpperCase() === 'USD' ? 'paypal' : 'yoco',
      }));
      setFeedback({ type: 'success', message: 'Package assigned. Create and send the payment link next.' });
    } catch (err) {
      setStudentLinkDialog((prev) => ({ ...prev, packageAssigning: false, error: err?.message || 'Failed to assign package.' }));
    }
  };

  const handleCreateAdminPackagePaymentLink = async () => {
    const bookingId = studentLinkDialog.packageBooking?._id;
    if (!bookingId) return;
    setStudentLinkDialog((prev) => ({ ...prev, paymentLinkCreating: true, error: '', paymentLink: '' }));
    try {
      const result = await createAdminBookingPaymentLink(bookingId, {
        provider: studentLinkDialog.paymentProvider,
        note: 'Admin created package payment link from Users panel',
      });
      const link = result?.checkout?.actionUrl || result?.payment?.link || '';
      setStudentLinkDialog((prev) => ({ ...prev, paymentLinkCreating: false, paymentLink: link }));
      setFeedback({ type: 'success', message: link ? 'Payment link created.' : 'Payment record created.' });
    } catch (err) {
      setStudentLinkDialog((prev) => ({ ...prev, paymentLinkCreating: false, error: err?.message || 'Failed to create payment link.' }));
    }
  };

  const handleLock = (user, action) => {
    if (!user) return;
    const lockAction = action === 'lock';
    setConfirmDialog({
      open: true,
      title: lockAction ? 'Lock account' : 'Unlock account',
      description: lockAction
        ? `Lock ${getDisplayName(user)}? They will no longer be able to sign in.`
        : `Unlock ${getDisplayName(user)} so they can sign in again?`,
      loading: false,
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          if (isBookingParentMirror(user)) {
            const result = lockAction
              ? await lockAdminBookingParent(user.bookingUserId)
              : await unlockAdminBookingParent(user.bookingUserId);
            if (result?.lmsUser) upsertUserLocally(result.lmsUser);
            await loadData();
          } else {
            const updated = lockAction ? await lockUserAccount(user._id) : await unlockUserAccount(user._id);
            upsertUserLocally(updated);
          }
          setFeedback({
            type: 'success',
            message: lockAction ? 'User locked successfully.' : 'User unlocked successfully.',
          });
          setConfirmDialog({ open: false, title: '', description: '', loading: false, onConfirm: null });
        } catch (err) {
          setConfirmDialog({ open: false, title: '', description: '', loading: false, onConfirm: null });
          setFeedback({ type: 'error', message: err?.message || 'Action failed.' });
        }
      },
    });
  };

  const handleDeactivateParent = (user) => {
    if (!user) return;
    setConfirmDialog({
      open: true,
      title: 'Deactivate parent',
      description: `Deactivate ${getDisplayName(user)}? This blocks parent portal access but keeps students, bookings, payments, and history intact.`,
      loading: false,
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          const result = await deactivateAdminBookingParent(user.bookingUserId);
          if (result?.lmsUser) upsertUserLocally(result.lmsUser);
          await loadData();
          setFeedback({ type: 'success', message: 'Parent account deactivated successfully.' });
          setConfirmDialog({ open: false, title: '', description: '', loading: false, onConfirm: null });
        } catch (err) {
          setConfirmDialog({ open: false, title: '', description: '', loading: false, onConfirm: null });
          setFeedback({ type: 'error', message: err?.message || 'Failed to deactivate parent account.' });
        }
      },
    });
  };
  const handleExport = async (format) => {
    try {
      const blob = await exportUsersFile({ format });
      downloadBlob(blob, `users.${format === 'json' ? 'json' : 'csv'}`);
      setFeedback({ type: 'success', message: `Exported users as ${format.toUpperCase()}.` });
    } catch (err) {
      setFeedback({ type: 'error', message: err?.message || 'Export failed.' });
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const summary = await importUsersFile(file);
      setImportSummary(summary);
      setFeedback({
        type: 'success',
        message: `Import complete. Created: ${summary.created}, Updated: ${summary.updated}, Skipped: ${summary.skipped}`,
      });
      await loadData();
    } catch (err) {
      setFeedback({ type: 'error', message: err?.message || 'Import failed.' });
    }
  };

  const handleResendWelcome = async () => {
    if (!resendDialog.user) return;
    setResendDialog((prev) => ({ ...prev, loading: true }));
    try {
      const password = cleanText(resendDialog.password) || undefined;
      if (isBookingParentMirror(resendDialog.user)) {
        await resendAdminBookingParentWelcome(resendDialog.user.bookingUserId, password);
      } else {
        await resendWelcomeEmail(resendDialog.user._id, password);
      }
      setFeedback({ type: 'success', message: 'Welcome email resent successfully.' });
      setResendDialog({ open: false, user: null, loading: false, password: '' });
    } catch (err) {
      setResendDialog((prev) => ({ ...prev, loading: false }));
      setFeedback({ type: 'error', message: err?.message || 'Failed to resend welcome email.' });
    }
  };

  const handleChangePassword = async () => {
    if (!passwordDialog.user) return;
    if (!passwordDialog.newPassword || passwordDialog.newPassword.length < 8) {
      setFeedback({ type: 'error', message: 'New password must be at least 8 characters.' });
      return;
    }
    if (passwordDialog.newPassword !== passwordDialog.confirmPassword) {
      setFeedback({ type: 'error', message: 'Passwords do not match.' });
      return;
    }
    setPasswordDialog((prev) => ({ ...prev, loading: true }));
    try {
      if (isBookingParentMirror(passwordDialog.user)) {
        await changeAdminBookingParentPassword(passwordDialog.user.bookingUserId, passwordDialog.newPassword);
      } else {
        await changeUserPassword({
          userId: passwordDialog.user._id,
          newPassword: passwordDialog.newPassword,
        });
      }
      setFeedback({ type: 'success', message: 'Password changed successfully.' });
      setPasswordDialog({ open: false, user: null, loading: false, newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPasswordDialog((prev) => ({ ...prev, loading: false }));
      setFeedback({ type: 'error', message: err?.message || 'Failed to change password.' });
    }
  };

  const isSelf = menuUser && currentUser && String(currentUser._id) === String(menuUser._id);
  const isAdminTarget = (menuUser?.role || '').toLowerCase() === 'admin' || (menuUser?.role || '').toLowerCase() === 'superadmin';
  const isParentMirrorTarget = isBookingParentMirror(menuUser);
  const disableAdminActions = isAdminTarget && !isSelf && !isSuperAdmin;
  const missingTutoringOptions = portalRequirementDialog.requirements.some(
    (item) => Array.isArray(item?.requiredFields) && item.requiredFields.includes('tutoringOptions'),
  );
  return (
    <DashboardLayout>
      <Stack spacing={3}>
        {loading && <LinearProgress />}

        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {feedback && (
          <Alert severity={feedback.type} onClose={() => setFeedback(null)}>
            {feedback.message}
          </Alert>
        )}

        {importSummary && (
          <Alert severity="info" onClose={() => setImportSummary(null)}>
            Imported users. Created: {importSummary.created}, Updated: {importSummary.updated}, Skipped: {importSummary.skipped}
          </Alert>
        )}

        <Paper sx={{ p: { xs: 2, md: 3 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', md: 'auto' } }}>
              <TextField
                label="Search users"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') applyUserSearch();
                }}
                placeholder="Search by name or email"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput || searchTerm ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={clearUserSearch}>
                        <ClearRoundedIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{ width: { xs: '100%', md: 320 } }}
              />
              <Button variant="outlined" startIcon={<SearchRoundedIcon />} onClick={applyUserSearch}>
                Search
              </Button>
            </Stack>

            <Box className="users-action-toolbar">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,application/json,text/csv"
                onChange={handleImport}
                style={{ display: 'none' }}
              />
              <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => openCreateDialog('student')}>
                Create LMS Student
              </Button>
              <Button variant="outlined" startIcon={<LinkRoundedIcon />} onClick={openBookingParentDialog}>
                Create Parent, Student & Package
              </Button>
              <Button
                variant="outlined"
                startIcon={<MoreVertRoundedIcon />}
                onClick={(event) => setToolbarMenuAnchor(event.currentTarget)}
                aria-controls={toolbarMenuAnchor ? 'users-utility-actions' : undefined}
                aria-expanded={toolbarMenuAnchor ? 'true' : undefined}
                aria-haspopup="menu"
              >
                Actions
              </Button>
            </Box>
          </Stack>
        </Paper>

        <Menu
          id="users-utility-actions"
          anchorEl={toolbarMenuAnchor}
          open={Boolean(toolbarMenuAnchor)}
          onClose={() => setToolbarMenuAnchor(null)}
        >
          <MenuItem onClick={() => { setToolbarMenuAnchor(null); handleExport('csv'); }}>
            <DownloadRoundedIcon fontSize="small" /> Export CSV
          </MenuItem>
          <MenuItem onClick={() => { setToolbarMenuAnchor(null); handleExport('json'); }}>
            <DownloadRoundedIcon fontSize="small" /> Export JSON
          </MenuItem>
          <MenuItem onClick={() => { setToolbarMenuAnchor(null); fileInputRef.current?.click(); }}>
            <UploadRoundedIcon fontSize="small" /> Import users
          </MenuItem>
          <MenuItem onClick={() => { setToolbarMenuAnchor(null); openRoleDialog(); }}>
            <AddRoundedIcon fontSize="small" /> Add administrator
          </MenuItem>
        </Menu>

        <Paper sx={{ p: 0 }}>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            {tabs.map((tab) => (
              <Tab key={tab.key} value={tab.key} label={tab.label} />
            ))}
          </Tabs>

          {activeTab === 'tutors' && (
            <Box sx={{ p: { xs: 2, md: 3 }, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  justifyContent="space-between"
                >
	                  <Box>
	                    <Typography variant="subtitle1" fontWeight={700}>
	                      Tutor search filters
	                    </Typography>
	                    <Typography variant="body2" color="text.secondary">
	                      Showing {visibleUsers.length} of {Array.isArray(tutorSearchRows) ? tutorSearchRows.length : groupedUsers.tutors.length} tutor users.
	                    </Typography>
	                  </Box>
	                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
	                    <Button
	                      variant="contained"
	                      size="small"
	                      startIcon={<SearchRoundedIcon />}
	                      onClick={searchTutors}
	                      disabled={tutorSearchLoading}
	                    >
	                      {tutorSearchLoading ? 'Searching...' : 'Search tutors'}
	                    </Button>
	                    <Button
	                      variant="outlined"
	                      size="small"
	                      startIcon={<ClearRoundedIcon />}
	                      onClick={clearTutorFilters}
	                      disabled={!activeTutorFilterCount && !Array.isArray(tutorSearchRows)}
	                    >
	                      Clear filters
	                    </Button>
	                  </Stack>
	                </Stack>
	                {tutorSearchError && (
	                  <Alert severity="error" onClose={() => setTutorSearchError('')}>
	                    {tutorSearchError}
	                  </Alert>
	                )}

                  <Box className="users-tutor-filter-sections">
                    <Box className="users-tutor-filter-section">
                      <Box>
                        <Typography className="users-tutor-filter-section-title">
                          Tutor expertise
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Filter by the tutor's teaching profile and current account status.
                        </Typography>
                      </Box>
                      <Box className="users-tutor-filter-grid">
                        <TutorFilterMultiSelect
                          label="Curriculum"
                          value={tutorFilters.curricula}
                          options={tutorFilterOptions.curricula}
                          onChange={(value) => updateTutorFilter('curricula', value)}
                        />
                        <TutorFilterMultiSelect
                          label="Subject"
                          value={tutorFilters.subjects}
                          options={tutorFilterOptions.subjects}
                          onChange={(value) => updateTutorFilter('subjects', value)}
                        />
                        <TutorFilterMultiSelect
                          label="Grade"
                          value={tutorFilters.grades}
                          options={tutorFilterOptions.grades}
                          onChange={(value) => updateTutorFilter('grades', value)}
                        />
                        <TutorFilterMultiSelect
                          label="Delivery"
                          value={tutorFilters.deliveryMethods}
                          options={tutorFilterOptions.deliveryMethods}
                          onChange={(value) => updateTutorFilter('deliveryMethods', value)}
                        />
                        <TutorFilterMultiSelect
                          label="Status"
                          value={tutorFilters.statuses}
                          options={tutorFilterOptions.statuses}
                          onChange={(value) => updateTutorFilter('statuses', value)}
                        />
                      </Box>
                    </Box>

                    <Box className="users-tutor-filter-section">
                      <Box>
                        <Typography className="users-tutor-filter-section-title">
                          Tutor location
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Narrow local and in-person tutor availability by service area.
                        </Typography>
                      </Box>
                      <Box className="users-tutor-filter-grid">
                        <TutorFilterMultiSelect
                          label="Country"
                          value={tutorFilters.countries}
                          options={tutorFilterOptions.countries}
                          onChange={(value) => updateTutorFilter('countries', value)}
                        />
                        <TutorFilterMultiSelect
                          label="Province"
                          value={tutorFilters.provinces}
                          options={tutorFilterOptions.provinces}
                          onChange={(value) => updateTutorFilter('provinces', value)}
                        />
                        <TutorFilterMultiSelect
                          label="Town / Area"
                          value={tutorFilters.towns}
                          options={tutorFilterOptions.towns}
                          onChange={(value) => updateTutorFilter('towns', value)}
                        />
                        <TutorFilterMultiSelect
                          label="Suburb"
                          value={tutorFilters.suburbs}
                          options={tutorFilterOptions.suburbs}
                          onChange={(value) => updateTutorFilter('suburbs', value)}
                        />
                        <TutorFilterMultiSelect
                          label="Area code"
                          value={tutorFilters.areaCodes}
                          options={tutorFilterOptions.areaCodes}
                          onChange={(value) => updateTutorFilter('areaCodes', value)}
                        />
                        <TutorFilterMultiSelect
                          label="Postal codes"
                          value={tutorFilters.postalCodes}
                          options={tutorFilterOptions.postalCodes}
                          onChange={(value) => updateTutorFilter('postalCodes', value)}
                        />
                        <TutorFilterTextField
                          label="Postal search"
                          value={tutorFilters.postalCode}
                          onChange={(event) => updateTutorFilter('postalCode', event.target.value)}
                          placeholder="Enter a postal code"
                        />
                        <TutorFilterTextField
                          label="Suburb / area search"
                          value={tutorFilters.area}
                          onChange={(event) => updateTutorFilter('area', event.target.value)}
                          placeholder="Suburb, town, province, or code"
                        />
                      </Box>
                    </Box>
                  </Box>
	              </Stack>
	            </Box>
          )}

          <Box sx={{ height: 560, width: '100%' }}>
            <DataGrid
              rows={visibleUsers}
              columns={columns}
              getRowId={(row) => row._id}
              disableRowSelectionOnClick
              density="comfortable"
              getRowHeight={() => 'auto'}
              sx={{
                border: 0,
                '& .MuiDataGrid-cell': { alignItems: 'center', py: 1 },
              }}
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
              }}
	              pageSizeOptions={[10, 25, 50]}
	              loading={loading || tutorSearchLoading}
              slots={{
                noRowsOverlay: () => <EmptyOverlay message="No users found for this category." />,
                noResultsOverlay: () => <EmptyOverlay message="No users match your search." />,
              }}
              onRowDoubleClick={({ row }) => openUserProfile(row)}
            />
          </Box>
        </Paper>
      </Stack>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            closeMenu();
            openUserProfile(menuUser);
          }}
        >
          <VisibilityRoundedIcon fontSize="small" sx={{ mr: 1 }} />
          View profile
        </MenuItem>
        {String(menuUser?.role || '').toLowerCase() === 'parent' && (
          [
            <MenuItem
              key="add-booking-student"
              onClick={() => {
                const selected = menuUser;
                closeMenu();
                openManualBookingStudentDialog(selected);
              }}
            >
              <AddRoundedIcon fontSize="small" sx={{ mr: 1 }} />
              Add booking student
            </MenuItem>,
            <MenuItem
              key="link-lms-student"
              onClick={() => {
                const selected = menuUser;
                closeMenu();
                openStudentLinkDialog(selected);
              }}
            >
              <LinkRoundedIcon fontSize="small" sx={{ mr: 1 }} />
              Link existing LMS student
            </MenuItem>,
          ]
        )}
        {String(menuUser?.role || '').toLowerCase() === 'tutor' && (
          [
            <MenuItem
              key="copy-public-tutor-profile-link"
              onClick={() => {
                const selected = menuUser;
                closeMenu();
                handleCreateTutorShareLink(selected);
              }}
            >
              <LinkRoundedIcon fontSize="small" sx={{ mr: 1 }} />
              Copy public profile link
            </MenuItem>,
            menuUser?.publicProfileEnabled ? (
              <MenuItem
                key="disable-public-tutor-profile-link"
                onClick={() => {
                  const selected = menuUser;
                  closeMenu();
                  handleDisableTutorShareLink(selected);
                }}
              >
                <LinkRoundedIcon fontSize="small" sx={{ mr: 1 }} />
                Disable public profile link
              </MenuItem>
            ) : null,
          ]
        )}
        <MenuItem
          onClick={() => {
            closeMenu();
            if (isParentMirrorTarget) {
              openEditBookingParentDialog(menuUser);
              return;
            }
            openEditDialog(menuUser);
          }}
          disabled={disableAdminActions}
        >
          <EditRoundedIcon fontSize="small" sx={{ mr: 1 }} />
          {isParentMirrorTarget ? 'Edit parent' : 'Edit user'}
        </MenuItem>
        {String(menuUser?.role || '').toLowerCase() === 'student' && ['invitation_pending', 'delivery_failed'].includes(menuUser?.accountActivationStatus) && (
          <MenuItem
            onClick={() => {
              const selected = menuUser;
              closeMenu();
              handleResendStudentActivation(selected);
            }}
          >
            <EmailRoundedIcon fontSize="small" sx={{ mr: 1 }} />
            Resend activation invitation
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            closeMenu();
            setResendDialog({ open: true, user: menuUser, loading: false, password: '' });
          }}
        >
          <EmailRoundedIcon fontSize="small" sx={{ mr: 1 }} />
          Resend welcome email
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            if (disableAdminActions) {
              setFeedback({ type: 'error', message: 'Only super administrators can change passwords for other admins.' });
              return;
            }
            setPasswordDialog({ open: true, user: menuUser, loading: false, newPassword: '', confirmPassword: '' });
          }}
        >
          <PasswordRoundedIcon fontSize="small" sx={{ mr: 1 }} />
          Change password
        </MenuItem>
        {menuUser?.isLocked ? (
          <MenuItem
            onClick={() => {
              closeMenu();
              handleLock(menuUser, 'unlock');
            }}
            disabled={disableAdminActions || isSelf}
          >
            <LockOpenRoundedIcon fontSize="small" sx={{ mr: 1 }} />
            Unlock account
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              closeMenu();
              handleLock(menuUser, 'lock');
            }}
            disabled={disableAdminActions || isSelf}
          >
            <LockRoundedIcon fontSize="small" sx={{ mr: 1 }} />
            Lock account
          </MenuItem>
        )}
        <Divider />
        {isParentMirrorTarget ? (
          <MenuItem
            onClick={() => {
              closeMenu();
              handleDeactivateParent(menuUser);
            }}
          >
            <DeleteRoundedIcon fontSize="small" sx={{ mr: 1 }} color="error" />
            Deactivate parent
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              closeMenu();
              if (isSelf) {
                setFeedback({ type: 'error', message: 'You cannot delete your own account.' });
                return;
              }
              if (disableAdminActions) {
                setFeedback({ type: 'error', message: 'Only super administrators can delete other admins.' });
                return;
              }
              handleDelete(menuUser);
            }}
            disabled={isSelf}
          >
            <DeleteRoundedIcon fontSize="small" sx={{ mr: 1 }} color="error" />
            Delete user
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={bookingParentDialog.open}
        onClose={() => !bookingParentDialog.loading && setBookingParentDialog((prev) => ({ ...prev, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{bookingParentDialog.mode === 'edit' ? 'Edit booking parent account' : 'Create booking parent account'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info">
              This creates a booking parent account and syncs the matching LMS parent mirror account.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="First name"
                  fullWidth
                  value={bookingParentDialog.name}
                  onChange={(event) => setBookingParentDialog((prev) => ({ ...prev, name: event.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Surname"
                  fullWidth
                  value={bookingParentDialog.surname}
                  onChange={(event) => setBookingParentDialog((prev) => ({ ...prev, surname: event.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Email"
                  fullWidth
                  value={bookingParentDialog.email}
                  onChange={(event) => setBookingParentDialog((prev) => ({ ...prev, email: event.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Phone"
                  fullWidth
                  value={bookingParentDialog.phone}
                  onChange={(event) => setBookingParentDialog((prev) => ({ ...prev, phone: event.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={COUNTRY_OPTIONS}
                  value={bookingParentDialog.country || 'South Africa'}
                  onChange={(_, value) => setBookingParentDialog((prev) => ({ ...prev, country: value || 'South Africa' }))}
                  renderInput={(params) => <TextField {...params} label="Country" />}
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBookingParentDialog((prev) => ({ ...prev, open: false }))} disabled={bookingParentDialog.loading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateBookingParent} disabled={bookingParentDialog.loading}>
            {bookingParentDialog.loading
              ? (bookingParentDialog.mode === 'edit' ? 'Saving...' : 'Creating...')
              : (bookingParentDialog.mode === 'edit' ? 'Save Parent' : 'Create Parent')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={studentLinkDialog.open}
        onClose={() => !studentLinkDialog.linking && setStudentLinkDialog((prev) => ({ ...prev, open: false }))}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {studentLinkDialog.mode === 'create' ? 'Add booking student and package' : 'Link existing LMS student to booking parent'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Parent: {getDisplayName(studentLinkDialog.parent)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {studentLinkDialog.parent?.email || '—'}
              </Typography>
            </Box>
            {studentLinkDialog.error && <Alert severity="error">{studentLinkDialog.error}</Alert>}
            {studentLinkDialog.mode === 'link' && (
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <TextField
                  label="Search LMS general students"
                  value={studentLinkDialog.query}
                  onChange={(event) => setStudentLinkDialog((prev) => ({ ...prev, query: event.target.value }))}
                  fullWidth
                  placeholder="Name, surname, or email"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') searchLinkableStudents(event.currentTarget.value);
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={<SearchRoundedIcon />}
                  onClick={() => searchLinkableStudents(studentLinkDialog.query)}
                  disabled={studentLinkDialog.loading}
                >
                  {studentLinkDialog.loading ? 'Searching...' : 'Search'}
                </Button>
              </Stack>
            )}
            <Grid container spacing={2}>
              {studentLinkDialog.mode === 'link' && (
              <Grid item xs={12} md={5}>
                <Paper variant="outlined" sx={{ maxHeight: 420, overflow: 'auto' }}>
                  {studentLinkDialog.rows.length === 0 && !studentLinkDialog.loading ? (
                    <Box sx={{ p: 2 }}>
                      <Typography variant="body2" color="text.secondary">No students found.</Typography>
                    </Box>
                  ) : (
                    studentLinkDialog.rows.map((student) => {
                      const selected = String(studentLinkDialog.selected?._id || '') === String(student._id);
                      return (
                        <Box
                          key={student._id}
                          onClick={() => selectLinkStudent(student)}
                          sx={{
                            p: 1.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            cursor: 'pointer',
                            bgcolor: selected ? 'action.selected' : 'background.paper',
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                            <Box>
                              <Typography variant="body2" fontWeight={700}>{student.name} {student.surname}</Typography>
                              <Typography variant="caption" color="text.secondary">{student.email}</Typography>
                            </Box>
                            <Chip
                              size="small"
                              label={student.linkedStatus === 'linked' ? 'Linked' : 'Not linked'}
                              color={student.linkedStatus === 'linked' ? 'warning' : 'success'}
                              variant="outlined"
                            />
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            Grade {student.grade || '—'} · {(student.curricula || []).join(', ') || 'No curriculum'} · {(student.subjects || []).join(', ') || 'No subjects'}
                          </Typography>
                          {student.missingFields?.length ? (
                            <Typography variant="caption" color="warning.main" display="block">
                              Missing: {student.missingFields.join(', ')}
                            </Typography>
                          ) : null}
                        </Box>
                      );
                    })
                  )}
                </Paper>
              </Grid>
              )}
              <Grid item xs={12} md={studentLinkDialog.mode === 'link' ? 7 : 12}>
                {studentLinkDialog.selected || studentLinkDialog.mode === 'create' ? (
                  <Stack spacing={2}>
                    <Alert severity="info">
                      Save the learner details first. Package selection creates a pending payment request; sessions only activate after payment is confirmed.
                    </Alert>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Learner first name"
                          fullWidth
                          value={studentLinkDialog.profilePatch.name || ''}
                          onChange={(event) => updateStudentLinkPatch('name', event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Learner surname"
                          fullWidth
                          value={studentLinkDialog.profilePatch.surname || ''}
                          onChange={(event) => updateStudentLinkPatch('surname', event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="School name"
                          fullWidth
                          value={studentLinkDialog.profilePatch.schoolName || ''}
                          onChange={(event) => updateStudentLinkPatch('schoolName', event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel id="link-grade-label">Grade</InputLabel>
                          <Select
                            labelId="link-grade-label"
                            label="Grade"
                            value={studentLinkDialog.profilePatch.grade || ''}
                            onChange={(event) => updateStudentLinkPatch('grade', event.target.value)}
                          >
                            <MenuItem value={0}>Grade R</MenuItem>
                            {gradeOptions.map((option) => <MenuItem key={option} value={option}>Grade {option}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel id="link-curriculum-label">Curriculum</InputLabel>
                          <Select
                            labelId="link-curriculum-label"
                            label="Curriculum"
                            value={studentLinkDialog.profilePatch.curriculum || ''}
                            onChange={(event) => updateStudentLinkPatch('curriculum', event.target.value)}
                          >
                            {curriculumOptions.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Curriculum level"
                          fullWidth
                          value={studentLinkDialog.profilePatch.curriculumLevel || ''}
                          onChange={(event) => updateStudentLinkPatch('curriculumLevel', event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Curriculum sub-level"
                          fullWidth
                          value={studentLinkDialog.profilePatch.curriculumSubLevel || ''}
                          onChange={(event) => updateStudentLinkPatch('curriculumSubLevel', event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Autocomplete
                          multiple
                          options={bookingStudentSubjectOptions.map((subject) => subject.name)}
                          value={studentLinkDialog.profilePatch.subjects || []}
                          onChange={(_, value) => {
                            updateStudentLinkPatch('subjects', value);
                            updateStudentLinkPatch('primarySubject', value?.[0] || '');
                          }}
                          renderInput={(params) => <TextField {...params} label="Subjects" helperText="Filtered by curriculum and grade." />}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          options={studentLinkDialog.profilePatch.subjects || []}
                          value={studentLinkDialog.profilePatch.primarySubject || ''}
                          onChange={(_, value) => updateStudentLinkPatch('primarySubject', value || '')}
                          renderInput={(params) => <TextField {...params} label="Main subject" />}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          options={COUNTRY_OPTIONS}
                          value={studentLinkDialog.profilePatch.country || 'South Africa'}
                          onChange={(_, value) => updateStudentLinkPatch('country', value || 'South Africa')}
                          renderInput={(params) => <TextField {...params} label="Country" />}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Province"
                          fullWidth
                          value={studentLinkDialog.profilePatch.province || ''}
                          onChange={(event) => updateStudentLinkPatch('province', event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Town"
                          fullWidth
                          value={studentLinkDialog.profilePatch.town || ''}
                          onChange={(event) => updateStudentLinkPatch('town', event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Suburb"
                          fullWidth
                          value={studentLinkDialog.profilePatch.suburb || ''}
                          onChange={(event) => updateStudentLinkPatch('suburb', event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Postal code"
                          fullWidth
                          value={studentLinkDialog.profilePatch.postalCode || ''}
                          onChange={(event) => updateStudentLinkPatch('postalCode', event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Address line 1"
                          fullWidth
                          value={studentLinkDialog.profilePatch.addressLine1 || ''}
                          onChange={(event) => updateStudentLinkPatch('addressLine1', event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel id="link-delivery-label">Delivery mode</InputLabel>
                          <Select
                            labelId="link-delivery-label"
                            label="Delivery mode"
                            value={studentLinkDialog.profilePatch.deliveryMode || ''}
                            onChange={(event) => updateStudentLinkPatch('deliveryMode', event.target.value)}
                          >
                            {bookingDeliveryOptions.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          options={bookingLanguageOptions}
                          value={studentLinkDialog.profilePatch.preferredLanguage || ''}
                          onChange={(_, value) => updateStudentLinkPatch('preferredLanguage', value || '')}
                          renderInput={(params) => <TextField {...params} label="Preferred language" />}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel id="link-gender-label">Preferred tutor gender</InputLabel>
                          <Select
                            labelId="link-gender-label"
                            label="Preferred tutor gender"
                            value={studentLinkDialog.profilePatch.preferredTutorGender || ''}
                            onChange={(event) => updateStudentLinkPatch('preferredTutorGender', event.target.value)}
                          >
                            <MenuItem value="">No preference</MenuItem>
                            <MenuItem value="male">Male</MenuItem>
                            <MenuItem value="female">Female</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Preferred lesson duration"
                          fullWidth
                          type="number"
                          value={studentLinkDialog.profilePatch.preferredLessonDuration || ''}
                          onChange={(event) => updateStudentLinkPatch('preferredLessonDuration', event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Preferred lesson frequency"
                          fullWidth
                          value={studentLinkDialog.profilePatch.preferredLessonFrequency || ''}
                          onChange={(event) => updateStudentLinkPatch('preferredLessonFrequency', event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Autocomplete
                          multiple
                          options={bookingTeachingStyleOptions}
                          value={studentLinkDialog.profilePatch.preferredTeachingStyles || []}
                          onChange={(_, value) => updateStudentLinkPatch('preferredTeachingStyles', value)}
                          renderInput={(params) => <TextField {...params} label="Preferred teaching styles" />}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Autocomplete
                          multiple
                          options={bookingLearningSupportOptions}
                          value={studentLinkDialog.profilePatch.learningSupportNeeds || []}
                          onChange={(_, value) => updateStudentLinkPatch('learningSupportNeeds', value)}
                          renderInput={(params) => <TextField {...params} label="Learning support needs" />}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Autocomplete
                          multiple
                          options={bookingSpecialNeedsOptions}
                          value={studentLinkDialog.profilePatch.specialNeedsSupport || []}
                          onChange={(_, value) => updateStudentLinkPatch('specialNeedsSupport', value)}
                          renderInput={(params) => <TextField {...params} label="Special needs support" />}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          multiple
                          options={bookingDaysOptions}
                          value={studentLinkDialog.profilePatch.availabilitySchedule?.daysAvailable || []}
                          onChange={(_, value) => updateStudentAvailabilityPatch('daysAvailable', value)}
                          renderInput={(params) => <TextField {...params} label="Preferred days" />}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          multiple
                          options={bookingTimeSlotOptions}
                          value={studentLinkDialog.profilePatch.availabilitySchedule?.timeSlots || []}
                          onChange={(_, value) => updateStudentAvailabilityPatch('timeSlots', value)}
                          renderInput={(params) => <TextField {...params} label="Preferred time slots" />}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Academic goals"
                          fullWidth
                          multiline
                          minRows={3}
                          value={studentLinkDialog.profilePatch.academicGoals || ''}
                          onChange={(event) => updateStudentLinkPatch('academicGoals', event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Admin notes"
                          fullWidth
                          multiline
                          minRows={2}
                          value={studentLinkDialog.profilePatch.notes || ''}
                          onChange={(event) => updateStudentLinkPatch('notes', event.target.value)}
                        />
                      </Grid>
                    </Grid>
                    {studentLinkDialog.selected.linkedStatus === 'linked' && (
                      <Alert severity="warning">
                        This student is already linked. Reassignment requires a reason and will move the active booking profile to this parent.
                      </Alert>
                    )}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={studentLinkDialog.forceReassign}
                          onChange={(event) => setStudentLinkDialog((prev) => ({ ...prev, forceReassign: event.target.checked }))}
                        />
                      }
                      label="Allow reassignment if already linked"
                    />
                    {studentLinkDialog.forceReassign && (
                      <TextField
                        label="Reassignment / audit reason"
                        fullWidth
                        value={studentLinkDialog.reason}
                        onChange={(event) => setStudentLinkDialog((prev) => ({ ...prev, reason: event.target.value }))}
                      />
                    )}
                    {studentLinkDialog.savedStudent ? (
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack spacing={2}>
                          <Typography variant="subtitle1" fontWeight={700}>Package and payment</Typography>
                          {studentLinkDialog.packageLoading ? <LinearProgress /> : null}
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={7}>
                              <FormControl fullWidth disabled={studentLinkDialog.packageLoading || studentLinkDialog.packageAssigning}>
                                <InputLabel id="admin-package-label">Package</InputLabel>
                                <Select
                                  labelId="admin-package-label"
                                  label="Package"
                                  value={studentLinkDialog.packageName || ''}
                                  onChange={(event) => setStudentLinkDialog((prev) => ({ ...prev, packageName: event.target.value }))}
                                >
                                  {(studentLinkDialog.packages || []).map((option) => (
                                    <MenuItem key={option.packageName} value={option.packageName}>
                                      {option.packageName} · {option.sessionsPerMonth} sessions · {formatAdminMoney(option.totalClientAmount, option.currency)}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <FormHelperText>Package sessions remain pending until payment is confirmed.</FormHelperText>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} md={5}>
                              <Button
                                fullWidth
                                variant="contained"
                                onClick={handleAssignAdminPackage}
                                disabled={!studentLinkDialog.packageName || studentLinkDialog.packageAssigning}
                                sx={{ minHeight: 56 }}
                              >
                                {studentLinkDialog.packageAssigning ? 'Assigning...' : 'Assign Package'}
                              </Button>
                            </Grid>
                          </Grid>
                          {studentLinkDialog.packageBooking ? (
                            <Stack spacing={1.5}>
                              <Alert severity="success">
                                Pending payment created for {studentLinkDialog.packageBooking.bookingNumber}. Amount due: {formatAdminMoney(
                                  studentLinkDialog.packageBooking.pricingSnapshot?.totalClientAmount,
                                  studentLinkDialog.packageBooking.billingCurrency || studentLinkDialog.packageBooking.pricingSnapshot?.currency,
                                )}
                              </Alert>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={5}>
                                  <FormControl fullWidth>
                                    <InputLabel id="admin-payment-provider-label">Payment provider</InputLabel>
                                    <Select
                                      labelId="admin-payment-provider-label"
                                      label="Payment provider"
                                      value={studentLinkDialog.paymentProvider}
                                      onChange={(event) => setStudentLinkDialog((prev) => ({ ...prev, paymentProvider: event.target.value }))}
                                    >
                                      <MenuItem value="yoco">Yoco / ZAR</MenuItem>
                                      <MenuItem value="paypal">PayPal / USD</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                                <Grid item xs={12} md={7}>
                                  <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleCreateAdminPackagePaymentLink}
                                    disabled={studentLinkDialog.paymentLinkCreating}
                                    sx={{ minHeight: 56 }}
                                  >
                                    {studentLinkDialog.paymentLinkCreating ? 'Creating link...' : 'Create Payment Link'}
                                  </Button>
                                </Grid>
                              </Grid>
                              {studentLinkDialog.paymentLink ? (
                                <TextField
                                  label="Payment link"
                                  fullWidth
                                  value={studentLinkDialog.paymentLink}
                                  InputProps={{ readOnly: true }}
                                />
                              ) : null}
                            </Stack>
                          ) : null}
                        </Stack>
                      </Paper>
                    ) : null}
                  </Stack>
                ) : (
                  <Alert severity="info">Select an LMS student to review the mapped booking profile.</Alert>
                )}
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStudentLinkDialog((prev) => ({ ...prev, open: false }))} disabled={studentLinkDialog.linking}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleLinkStudentToParent}
            disabled={(studentLinkDialog.mode === 'link' && !studentLinkDialog.selected) || studentLinkDialog.linking}
          >
            {studentLinkDialog.linking
              ? 'Saving...'
              : studentLinkDialog.mode === 'create'
                ? 'Save Student'
                : 'Save / Link Student'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={roleDialog.open} onClose={closeRoleDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Select user role</DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth>
            <InputLabel id="create-role-label">Role</InputLabel>
            <Select
              labelId="create-role-label"
              label="Role"
              value={roleDialog.role}
              onChange={(event) => setRoleDialog((prev) => ({ ...prev, role: event.target.value }))}
            >
              {roleOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRoleDialog}>Cancel</Button>
          <Button variant="contained" onClick={confirmRoleDialog} disabled={!roleDialog.role}>
            Continue
          </Button>
        </DialogActions>
      </Dialog>

        <Dialog
          open={formDialog.open}
          onClose={() => {
            if (!formDialog.loading) {
              setFormDialog({ open: false, mode: 'create', loading: false });
              resetForm();
            }
          }}
          maxWidth="lg"
          fullWidth
        >
        <DialogTitle>{formDialog.mode === 'create' && formState.role === 'student' ? 'Create LMS Student' : formDialog.mode === 'create' ? 'Add user' : 'Edit user'}</DialogTitle>
        <DialogContent dividers>
          {legacyStudentEditWarnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This legacy student profile is missing: {legacyStudentEditWarnings.join(', ')}. You can save tutor or subject changes now; complete these details when they become available.
            </Alert>
          )}
          {isStudentCreate ? (
            <Stack spacing={2} sx={{ alignItems: 'center' }}>
              <Stepper activeStep={studentStep} alternativeLabel sx={{ width: '100%', maxWidth: 1100 }}>
                {studentStepLabels.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
              {studentStep === 4 ? (
                studentEditSummary ? (
                  <Stack spacing={2}>
                    <Typography variant="subtitle1">Choose a step to edit</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {studentStepLabels.slice(0, 4).map((label, index) => (
                        <Button
                          key={label}
                          variant="outlined"
                          onClick={() => {
                            setStudentStep(index);
                            setStudentEditSummary(false);
                          }}
                        >
                          {label}
                        </Button>
                      ))}
                    </Stack>
                  </Stack>
                ) : (
                  <Stack spacing={2}>
                    <Typography variant="subtitle1">Review student details</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>Name:</strong> {cleanText(formState.name) || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>Surname:</strong> {cleanText(formState.surname) || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>Email:</strong> {cleanText(formState.email) || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>Program:</strong> {(formState.program || 'general').toUpperCase()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>Grade:</strong> {formState.grade ? formatStudentGrade(parseStudentGrade(formState.grade)) : '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>Subjects:</strong> {getSubjectNames(formState.subjectIds) || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>Tutors:</strong> {getTutorNames(formState.tutorIds) || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>Phone:</strong> {combinePhoneValue(formState.phoneCountryCode, formState.phoneNationalNumber) || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2">
                          <strong>Location:</strong> {formatLocation(formState)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>Tutoring options:</strong>
                          {formState.tutoringOptions.length ? ` ${formState.tutoringOptions.join(', ')}` : ' —'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>Curricula:</strong> {splitListInput(formState.curricula).join(', ') || '—'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Stack>
                )
              ) : (
                <Grid container spacing={2}>
                  {studentStep === 0 && (
                      <Grid container spacing={2} sx={{ maxWidth: 1100, mx: 'auto', width: '100%' }}>
                      <Grid item xs={12} md={6}>
                          <TextField
                            label="First name"
                            fullWidth
                            value={formState.name}
                            onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
                            error={Boolean(formErrors.name)}
                            helperText={formErrors.name}
                          />
                      </Grid>
                      <Grid item xs={12} md={6}>
                          <TextField
                            label="Surname"
                            fullWidth
                            value={formState.surname}
                            onChange={(event) => setFormState((prev) => ({ ...prev, surname: event.target.value }))}
                            error={Boolean(formErrors.surname)}
                            helperText={formErrors.surname}
                          />
                      </Grid>
                      <Grid item xs={12}>
                          <TextField
                            label="Email"
                            fullWidth
                            value={formState.email}
                            onChange={(event) => setFormState((prev) => ({ ...prev, email: event.target.value }))}
                            error={Boolean(formErrors.email)}
                            helperText={formErrors.email}
                          />
                      </Grid>
                    </Grid>
                  )}
                  {studentStep === 1 && (
                      <Grid container spacing={2} sx={{ maxWidth: 1100, mx: 'auto', width: '100%' }}>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth error={Boolean(formErrors.program)}>
                          <InputLabel id="student-program-label">Programme</InputLabel>
                          <Select
                            labelId="student-program-label"
                            label="Programme"
                            value={formState.program}
                            onChange={(event) => setFormState((prev) => ({
                              ...prev,
                              program: event.target.value,
                              tutorIds: [],
                            }))}
                          >
                            {studentProgramOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                            ))}
                          </Select>
                          {formErrors.program && <FormHelperText>{formErrors.program}</FormHelperText>}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="School name"
                          fullWidth
                          value={formState.schoolName}
                          onChange={(event) => setFormState((prev) => ({ ...prev, schoolName: event.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={Boolean(formErrors.grade)}
                          sx={{
                            width: '100%',
                            minWidth: 260,
                            '& .MuiInputBase-root': { width: '100%', minWidth: 260 },
                            '& .MuiSelect-select': { width: '100%', minWidth: 260 }
                          }}
                        >
                          <InputLabel id="grade-label">Grade</InputLabel>
                          <Select
                            labelId="grade-label"
                            label="Grade"
                            value={formState.grade}
                            onChange={(event) => setFormState((prev) => ({ ...prev, grade: event.target.value }))}
                            SelectDisplayProps={{ style: { width: '100%', minWidth: 260 } }}
                          >
                            {gradeOptions.map((grade) => (
                              <MenuItem key={grade} value={grade}>
                                {grade === 'R' ? 'Grade R' : `Grade ${grade}`}
                              </MenuItem>
                            ))}
                          </Select>
                          {formErrors.grade && <FormHelperText>{formErrors.grade}</FormHelperText>}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={Boolean(formErrors.curricula)}
                          sx={{
                            width: '100%',
                            minWidth: 260,
                            '& .MuiInputBase-root': { width: '100%', minWidth: 260 },
                            '& .MuiSelect-select': { width: '100%', minWidth: 260 }
                          }}
                        >
                          <InputLabel id="student-curricula-label">Curricula</InputLabel>
                          <Select
                            labelId="student-curricula-label"
                            multiple
                            value={formState.curricula}
                            onChange={(event) => setFormState((prev) => ({ ...prev, curricula: event.target.value }))}
                            input={<OutlinedInput label="Curricula" />}
                            renderValue={(selected) => (selected.length ? selected.join(', ') : 'None')}
                            SelectDisplayProps={{ style: { width: '100%', minWidth: 260 } }}
                          >
                            {getAllowedCurriculaForCountry(formState.country).map((option) => (
                              <MenuItem key={option} value={option}>
                                <Checkbox checked={formState.curricula.includes(option)} />
                                <ListItemText primary={option} />
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            {formErrors.curricula || 'Select curriculum first so subjects are filtered correctly.'}
                          </FormHelperText>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={Boolean(formErrors.subjectIds)}
                          sx={{
                            width: '100%',
                            minWidth: 260,
                            '& .MuiInputBase-root': { width: '100%', minWidth: 260 },
                            '& .MuiSelect-select': { width: '100%', minWidth: 260 }
                          }}
                        >
                          <InputLabel id="subjects-label">Subjects</InputLabel>
                          <Select
                            labelId="subjects-label"
                            multiple
                            value={formState.subjectIds}
                            onChange={(event) => setFormState((prev) => ({ ...prev, subjectIds: event.target.value }))}
                            input={<OutlinedInput label="Subjects" />}
                            renderValue={(selected) => {
                              if (!selected.length) return 'None';
                              return selected
                                .map((id) => {
                                  const match = subjects.find((subject) => String(subject._id) === String(id));
                                  return match ? match.name : null;
                                })
                                .filter(Boolean)
                                .join(', ');
                            }}
                            SelectDisplayProps={{ style: { width: '100%', minWidth: 260 } }}
                            disabled={!hasCurriculumSubjectContext}
                          >
                            {curriculumFilteredSubjects.map((subject) => (
                              <MenuItem key={subject._id} value={subject._id}>
                                <Checkbox checked={formState.subjectIds.includes(subject._id)} />
                                <ListItemText primary={subject.name} />
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            {formErrors.subjectIds || (!hasCurriculumSubjectContext ? 'Select curriculum and grade before choosing subjects.' : 'Subjects are filtered by selected curriculum and grade.')}
                          </FormHelperText>
                        </FormControl>
                      </Grid>
                    </Grid>
                  )}
                  {studentStep === 2 && (
                    <Grid item xs={12}>
                        <Box sx={{ width: '100%', maxWidth: 960, mx: 'auto' }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={7}>
                            <Autocomplete
                              options={COUNTRY_OPTIONS}
                              value={formState.country || null}
                              onChange={(_, country) => setFormState((prev) => {
                                const nextCountry = country || '';
                                const isLocal = nextCountry === 'South Africa';
                                return {
                                  ...prev,
                                  country: nextCountry,
                                  phoneCountryCode: getDefaultPhoneCodeForCountry(nextCountry),
                                  tutoringOptions: isLocal ? prev.tutoringOptions : ['online'],
                                  curricula: getAllowedCurriculaForCountry(nextCountry).includes(prev.curricula[0])
                                    ? prev.curricula
                                    : [],
                                  province: isLocal ? prev.province : '',
                                  postalCode: isLocal ? prev.postalCode : '',
                                  addressLine1: isLocal ? prev.addressLine1 : '',
                                };
                              })}
                              renderInput={(params) => (
                                <TextField {...params} label="Country" error={Boolean(formErrors.country)} helperText={formErrors.country} />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4} md={2}>
                            <Autocomplete
                              options={PHONE_CODE_SEARCH_OPTIONS}
                              value={PHONE_CODE_SEARCH_OPTIONS.find((option) => option.dialCode === formState.phoneCountryCode) || null}
                              onChange={(_, option) => setFormState((prev) => ({ ...prev, phoneCountryCode: option?.dialCode || '' }))}
                              getOptionLabel={(option) => `${option.flag} ${option.dialCode}`}
                              isOptionEqualToValue={(option, value) => option.iso2 === value.iso2}
                              renderInput={(params) => <TextField {...params} label="Code" />}
                            />
                          </Grid>
                          <Grid item xs={12} sm={8} md={3}>
                            <TextField
                              label="Phone number"
                              fullWidth
                              value={formState.phoneNationalNumber}
                              onChange={(event) => setFormState((prev) => ({ ...prev, phoneNationalNumber: event.target.value.replace(/[^\d\s()-]/g, '') }))}
                              error={Boolean(formErrors.phone)}
                              helperText={formErrors.phone || 'Required for account and lesson communication.'}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <FormControl fullWidth error={Boolean(formErrors.tutoringOptions)}>
                              <InputLabel id="student-delivery-label">Delivery methods</InputLabel>
                              <Select
                                labelId="student-delivery-label"
                                multiple
                                label="Delivery methods"
                                value={formState.tutoringOptions}
                                onChange={(event) => setFormState((prev) => ({ ...prev, tutoringOptions: event.target.value }))}
                                disabled={formState.country !== 'South Africa'}
                                renderValue={(selected) => selected.join(', ')}
                              >
                                {tutoringChoices.map((option) => (
                                  <MenuItem key={option} value={option}>
                                    <Checkbox checked={formState.tutoringOptions.includes(option)} />
                                    <ListItemText primary={option === 'in-person' ? 'In-person' : 'Online'} />
                                  </MenuItem>
                                ))}
                              </Select>
                              <FormHelperText>{formErrors.tutoringOptions || (formState.country !== 'South Africa' ? 'International students use online tutoring.' : '')}</FormHelperText>
                            </FormControl>
                          </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Province"
                          fullWidth
                          value={formState.province}
                          error={Boolean(formErrors.province)}
                          helperText={formErrors.province}
                          onChange={(event) => setFormState((prev) => ({ ...prev, province: event.target.value }))}
                          disabled={formState.country !== 'South Africa'}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Postal code"
                          fullWidth
                          value={formState.postalCode}
                          error={Boolean(formErrors.postalCode)}
                          helperText={formErrors.postalCode}
                          onChange={(event) => setFormState((prev) => ({ ...prev, postalCode: event.target.value }))}
                          disabled={formState.country !== 'South Africa'}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Town / City"
                          fullWidth
                          value={formState.town}
                          onChange={(event) => setFormState((prev) => ({ ...prev, town: event.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Suburb"
                          fullWidth
                          value={formState.suburb}
                          onChange={(event) => setFormState((prev) => ({ ...prev, suburb: event.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Private address line"
                          fullWidth
                          value={formState.addressLine1}
                          onChange={(event) => setFormState((prev) => ({ ...prev, addressLine1: event.target.value }))}
                          disabled={formState.country !== 'South Africa'}
                        />
                      </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  )}
                  {studentStep === 3 && (
                    <Grid item xs={12}>
                        <Box sx={{ width: '100%', maxWidth: 960, mx: 'auto' }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Autocomplete options={bookingLanguageOptions} value={formState.preferredLanguage || null} onChange={(_, value) => setFormState((prev) => ({ ...prev, preferredLanguage: value || '' }))} renderInput={(params) => <TextField {...params} label="Preferred language" />} />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                              <InputLabel id="student-preferred-gender-label">Preferred tutor gender</InputLabel>
                              <Select labelId="student-preferred-gender-label" label="Preferred tutor gender" value={formState.preferredTutorGender} onChange={(event) => setFormState((prev) => ({ ...prev, preferredTutorGender: event.target.value, tutorIds: [] }))}>
                                <MenuItem value="">No preference</MenuItem><MenuItem value="female">Female</MenuItem><MenuItem value="male">Male</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12}>
                            <Autocomplete multiple options={bookingTeachingStyleOptions} value={formState.preferredTeachingStyles} onChange={(_, value) => setFormState((prev) => ({ ...prev, preferredTeachingStyles: value, tutorIds: [] }))} renderInput={(params) => <TextField {...params} label="Preferred teaching styles" />} />
                          </Grid>
                          <Grid item xs={12}>
                            <Autocomplete multiple options={bookingLearningSupportOptions} value={formState.learningSupportNeeds} onChange={(_, value) => setFormState((prev) => ({ ...prev, learningSupportNeeds: value, tutorIds: [] }))} renderInput={(params) => <TextField {...params} label="Learning support needs" />} />
                          </Grid>
                          <Grid item xs={12}>
                            <Autocomplete multiple options={bookingSpecialNeedsOptions} value={formState.specialNeedsSupport} onChange={(_, value) => setFormState((prev) => ({ ...prev, specialNeedsSupport: value, tutorIds: [] }))} renderInput={(params) => <TextField {...params} label="Special needs support" />} />
                          </Grid>
                          <Grid item xs={12} md={6}><Autocomplete multiple options={bookingDaysOptions} value={formState.availabilitySchedule.daysAvailable || []} onChange={(_, value) => setFormState((prev) => ({ ...prev, availabilitySchedule: { ...prev.availabilitySchedule, daysAvailable: value } }))} renderInput={(params) => <TextField {...params} label="Preferred days" />} /></Grid>
                          <Grid item xs={12} md={6}><Autocomplete multiple options={bookingTimeSlotOptions} value={formState.availabilitySchedule.timeSlots || []} onChange={(_, value) => setFormState((prev) => ({ ...prev, availabilitySchedule: { ...prev.availabilitySchedule, timeSlots: value } }))} renderInput={(params) => <TextField {...params} label="Preferred time slots" />} /></Grid>
                          <Grid item xs={12} md={6}><TextField label="Preferred lesson duration (hours)" type="number" fullWidth value={formState.preferredLessonDuration} onChange={(event) => setFormState((prev) => ({ ...prev, preferredLessonDuration: event.target.value }))} /></Grid>
                          <Grid item xs={12} md={6}><TextField label="Preferred lesson frequency" fullWidth value={formState.preferredLessonFrequency} onChange={(event) => setFormState((prev) => ({ ...prev, preferredLessonFrequency: event.target.value }))} /></Grid>
                          <Grid item xs={12}><TextField label="Academic goals" fullWidth multiline minRows={2} value={formState.academicGoals} onChange={(event) => setFormState((prev) => ({ ...prev, academicGoals: event.target.value }))} /></Grid>
                          <Grid item xs={12}><TextField label="Admin notes" fullWidth multiline minRows={2} value={formState.studentNotes} onChange={(event) => setFormState((prev) => ({ ...prev, studentNotes: event.target.value }))} /></Grid>
                          {studentV2Available ? <Grid item xs={12}>
                            <Button variant="outlined" startIcon={<SearchRoundedIcon />} onClick={loadStudentTutorEligibility} disabled={studentTutorEligibilityLoading}>
                              {studentTutorEligibilityLoading ? 'Checking tutors...' : 'Find eligible tutors'}
                            </Button>
                          </Grid> : (
                            <Grid item xs={12}>
                              <Alert severity="warning" sx={{ mb: 2 }}>The safe student workflow is not enabled for this admin role. Tutor choices use the legacy fallback.</Alert>
                              <TutorDetailsAutocomplete
                                label="Allocate tutors"
                                value={formState.tutorIds}
                                options={availableTutorOptions}
                                allTutors={allTutors}
                                subjectsCatalog={subjects}
                                onChange={(tutorIds) => setFormState((prev) => ({ ...prev, tutorIds }))}
                              />
                            </Grid>
                          )}
                          {studentV2Available && studentTutorEligibilityError && <Grid item xs={12}><Alert severity="error">{studentTutorEligibilityError}</Alert></Grid>}
                          {studentV2Available && studentTutorEligibility.length > 0 && (
                            <Grid item xs={12}>
                              {studentTutorEligibility.some((item) => !item.eligible) && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                  Excluded tutors: {studentTutorEligibility.filter((item) => !item.eligible).slice(0, 5).map((item) => `${item.tutor?.name || 'Tutor'} (${(item.hardFailures || []).join('; ')})`).join(' · ')}
                                </Alert>
                              )}
                              <TutorDetailsAutocomplete
                                label="Allocate eligible tutors"
                                value={formState.tutorIds}
                                options={studentTutorEligibility.filter((item) => item.eligible).map((item) => ({ ...item.tutor, matchScore: item.matchScore, advisories: item.advisories || [] }))}
                                allTutors={studentTutorEligibility.map((item) => item.tutor)}
                                subjectsCatalog={subjects}
                                error={Boolean(formErrors.tutorIds)}
                                helperText={`${studentTutorEligibility.filter((item) => item.eligible).length} eligible; ${studentTutorEligibility.filter((item) => !item.eligible).length} excluded by hard requirements.`}
                                onChange={(tutorIds) => setFormState((prev) => ({ ...prev, tutorIds }))}
                              />
                              {studentTutorEligibility.some((item) => item.eligible && (formState.tutorIds || []).map(String).includes(String(item.tutor?._id)) && item.advisories?.length > 0) && (
                                <TextField
                                  label="Tutor preference override reason"
                                  fullWidth
                                  multiline
                                  minRows={2}
                                  sx={{ mt: 2 }}
                                  value={formState.tutorOverrideReason}
                                  onChange={(event) => setFormState((prev) => ({ ...prev, tutorOverrideReason: event.target.value }))}
                                  error={Boolean(formErrors.tutorOverrideReason)}
                                  helperText={formErrors.tutorOverrideReason || 'Required because the selected tutor has advisory preference mismatches. Hard eligibility failures cannot be overridden.'}
                                />
                              )}
                            </Grid>
                          )}
                          {studentActivationAvailable && (
                            <Grid item xs={12}><Alert severity="info">The learner will receive a secure activation link and choose their own password.</Alert></Grid>
                          )}
                      {formDialog.mode === 'create' && (
                        formState.accountAccessMode === 'manual' ? <>
                          <Grid item xs={12}>
                            <TextField
                              label="Password"
                              type="password"
                              fullWidth
                              value={formState.password}
                              onChange={(event) => setFormState((prev) => ({ ...prev, password: event.target.value }))}
                              error={Boolean(formErrors.password)}
                              helperText={formErrors.password || 'Minimum 8 characters.'}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              label="Confirm password"
                              type="password"
                              fullWidth
                              value={formState.confirmPassword}
                              onChange={(event) => setFormState((prev) => ({ ...prev, confirmPassword: event.target.value }))}
                              error={Boolean(formErrors.confirmPassword)}
                              helperText={formErrors.confirmPassword}
                            />
                          </Grid>
                        </> : null
                      )}
                        </Grid>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              )}
            </Stack>
          ) : isTutorCreate ? (
            <Stack spacing={2}>
              <Stepper activeStep={tutorStep} alternativeLabel>
                {tutorStepLabels.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
              {tutorStep === 4 ? (
                tutorEditSummary ? (
                  <Stack spacing={2}>
                    <Typography variant="subtitle1">Choose a step to edit</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {tutorStepLabels.slice(0, 4).map((label, index) => (
                        <Button
                          key={label}
                          variant="outlined"
                          onClick={() => {
                            setTutorStep(index);
                            setTutorEditSummary(false);
                          }}
                        >
                          {label}
                        </Button>
                      ))}
                    </Stack>
                  </Stack>
                ) : (
                  <Stack spacing={2}>
                    <Typography variant="subtitle1">Review tutor details</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>Name:</strong> {cleanText(formState.name) || 'ƒ?"'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>Surname:</strong> {cleanText(formState.surname) || 'ƒ?"'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>Email:</strong> {cleanText(formState.email) || 'ƒ?"'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>Program:</strong> {(formState.program || 'general').toUpperCase()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>Portals:</strong> {normalizeTutorPortals(formState.portals).join(', ')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>Active portal:</strong> {pickFirstPortal(formState.portals, formState.activePortal || 'tutor')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>Grade:</strong> {formState.grade || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>Subjects:</strong> {getSubjectNames(formState.subjectIds) || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>Curricula:</strong> {splitListInput(formState.curricula).join(', ') || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>Phone:</strong> {combinePhoneValue(formState.phoneCountryCode, formState.phoneNationalNumber) || cleanText(formState.phone) || 'ƒ?"'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2">
                          <strong>Location:</strong> {formatLocation(formState)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>Tutoring options:</strong>
                          {formState.tutoringOptions.length ? ` ${formState.tutoringOptions.join(', ')}` : ' ƒ?"'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Stack>
                )
              ) : (
                <Grid container spacing={2}>
                  {tutorStep === 0 && (
                    <Grid container spacing={2} sx={{ maxWidth: 1100, mx: 'auto', width: '100%' }}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="First name"
                          fullWidth
                          value={formState.name}
                          onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
                          error={Boolean(formErrors.name)}
                          helperText={formErrors.name}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Surname"
                          fullWidth
                          value={formState.surname}
                          onChange={(event) => setFormState((prev) => ({ ...prev, surname: event.target.value }))}
                          error={Boolean(formErrors.surname)}
                          helperText={formErrors.surname}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Email"
                          fullWidth
                          value={formState.email}
                          onChange={(event) => setFormState((prev) => ({ ...prev, email: event.target.value }))}
                          error={Boolean(formErrors.email)}
                          helperText={formErrors.email}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="ID / passport / driver license number"
                          fullWidth
                          value={formState.identityNumber}
                          onChange={(event) => setFormState((prev) => ({ ...prev, identityNumber: event.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          label="Date of birth"
                          type="date"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={formState.dateOfBirth}
                          onChange={(event) => setFormState((prev) => ({ ...prev, dateOfBirth: event.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <InputLabel id="tutor-gender-label">Gender</InputLabel>
                          <Select
                            labelId="tutor-gender-label"
                            label="Gender"
                            value={formState.gender}
                            onChange={(event) => setFormState((prev) => ({ ...prev, gender: event.target.value }))}
                          >
                            <MenuItem value="">Not specified</MenuItem>
                            <MenuItem value="male">Male</MenuItem>
                            <MenuItem value="female">Female</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  )}
                  {tutorStep === 1 && (
                    <Grid container spacing={2} sx={{ maxWidth: 1100, mx: 'auto', width: '100%' }}>
                      <Grid item xs={12}><Alert severity="info">Tutor type: General Tutor. Portal access is fixed to the General Tutor portal.</Alert></Grid>
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={Boolean(formErrors.grade)}
                          sx={{
                            width: '100%',
                            minWidth: 260,
                            '& .MuiInputBase-root': { width: '100%', minWidth: 260 },
                            '& .MuiSelect-select': { width: '100%', minWidth: 260 }
                          }}
                        >
                          <InputLabel id="tutor-grade-label">Grade</InputLabel>
                          <Select
                            labelId="tutor-grade-label"
                            label="Grade"
                            value={formState.grade}
                            onChange={(event) => setFormState((prev) => ({ ...prev, grade: event.target.value }))}
                            SelectDisplayProps={{ style: { width: '100%', minWidth: 260 } }}
                          >
                            {gradeOptions.map((grade) => (
                              <MenuItem key={grade} value={grade}>
                                {grade}
                              </MenuItem>
                            ))}
                          </Select>
                          {formErrors.grade && <FormHelperText>{formErrors.grade}</FormHelperText>}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                          <FormControl
                            fullWidth
                            error={Boolean(formErrors.curricula)}
                            sx={{
                              width: '100%',
                              minWidth: 260,
                              '& .MuiInputBase-root': { width: '100%', minWidth: 260 },
                              '& .MuiSelect-select': { width: '100%', minWidth: 260 }
                            }}
                          >
                          <InputLabel id="tutor-curricula-label">Curricula</InputLabel>
                          <Select
                            labelId="tutor-curricula-label"
                            multiple
                            value={formState.curricula}
                            onChange={(event) => setFormState((prev) => ({ ...prev, curricula: event.target.value }))}
                            input={<OutlinedInput label="Curricula" />}
                            renderValue={(selected) => (selected.length ? selected.join(', ') : 'None')}
                            SelectDisplayProps={{ style: { width: '100%', minWidth: 260 } }}
                          >
                            {getAllowedCurriculaForCountry(formState.country).map((option) => (
                              <MenuItem key={option} value={option}>
                                <Checkbox checked={formState.curricula.includes(option)} />
                                <ListItemText primary={option} />
                              </MenuItem>
                            ))}
                          </Select>
                          {formErrors.curricula && <FormHelperText>{formErrors.curricula}</FormHelperText>}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={Boolean(formErrors.subjectIds)}
                          sx={{
                            width: '100%',
                            minWidth: 260,
                            '& .MuiInputBase-root': { width: '100%', minWidth: 260 },
                            '& .MuiSelect-select': { width: '100%', minWidth: 260 }
                          }}
                        >
                          <InputLabel id="tutor-subjects-label">Subjects</InputLabel>
                          <Select
                            labelId="tutor-subjects-label"
                            multiple
                            value={formState.subjectIds}
                            onChange={(event) => setFormState((prev) => ({ ...prev, subjectIds: event.target.value }))}
                            input={<OutlinedInput label="Subjects" />}
                            renderValue={(selected) => {
                              if (!selected.length) return 'None';
                              return selected
                                .map((id) => {
                                  const match = subjects.find((subject) => String(subject._id) === String(id));
                                  return match ? match.name : null;
                                })
                                .filter(Boolean)
                                .join(', ');
                              }}
                              SelectDisplayProps={{ style: { width: '100%', minWidth: 260 } }}
                            >
                            {curriculumFilteredSubjects.map((subject) => (
                              <MenuItem key={subject._id} value={subject._id}>
                                <Checkbox checked={formState.subjectIds.includes(subject._id)} />
                                <ListItemText primary={subject.name} />
                              </MenuItem>
                            ))}
                          </Select>
                          {formErrors.subjectIds && <FormHelperText>{formErrors.subjectIds}</FormHelperText>}
                        </FormControl>
                      </Grid>
                    </Grid>
                  )}
                  {tutorStep === 2 && (
                    <Grid item xs={12}>
                      <Box sx={{ width: '100%', maxWidth: 960, mx: 'auto' }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={4}>
                                <TextField
                                  select
                                  label="Phone code"
                                  fullWidth
                                  value={formState.phoneCountryCode}
                                  onChange={(event) =>
                                    setFormState((prev) => ({
                                      ...prev,
                                      phoneCountryCode: event.target.value,
                                      phone: combinePhoneValue(event.target.value, prev.phoneNationalNumber),
                                    }))
                                  }
                                >
                                  {PHONE_CODE_SEARCH_OPTIONS.map((option) => (
                                    <MenuItem key={`${option.country}-${option.dialCode}`} value={option.dialCode}>
                                      {option.flag} {option.dialCode} {option.country}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>
                              <Grid item xs={12} md={8}>
                                <TextField
                                  label="Phone number"
                                  fullWidth
                                  value={formState.phoneNationalNumber}
                                  onChange={(event) =>
                                    setFormState((prev) => ({
                                      ...prev,
                                      phoneNationalNumber: event.target.value,
                                      phone: combinePhoneValue(prev.phoneCountryCode, event.target.value),
                                    }))
                                  }
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                      <Grid item xs={12}>
                        <Autocomplete
                          options={COUNTRY_OPTIONS}
                          value={formState.country || null}
                          onChange={(event, value) =>
                            setFormState((prev) => {
                              const nextCountry = value || '';
                              const allowedCurricula = getAllowedCurriculaForCountry(nextCountry);
                              const nextCurricula = Array.isArray(prev.curricula)
                                ? prev.curricula.filter((entry) => allowedCurricula.includes(entry))
                                : [];
                              const nextPhoneCode = getDefaultPhoneCodeForCountry(nextCountry);
                              return {
                                ...prev,
                                country: nextCountry,
                                phoneCountryCode: nextPhoneCode,
                                phone: combinePhoneValue(nextPhoneCode, prev.phoneNationalNumber),
                                curricula: nextCurricula,
                              };
                            })
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Country"
                              fullWidth
                              error={Boolean(formErrors.country)}
                              helperText={formErrors.country}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Private address line"
                          fullWidth
                          value={formState.addressLine1}
                          onChange={(event) => setFormState((prev) => ({ ...prev, addressLine1: event.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Province"
                          fullWidth
                          value={formState.province}
                          onChange={(event) => setFormState((prev) => ({ ...prev, province: event.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Area code"
                          fullWidth
                          value={formState.areaCode}
                          onChange={(event) => setFormState((prev) => ({ ...prev, areaCode: event.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Postal codes"
                          fullWidth
                          helperText="Comma separated. Used for booking matching."
                          value={formState.postalCodes}
                          onChange={(event) => setFormState((prev) => ({ ...prev, postalCodes: event.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Town / City"
                          fullWidth
                          value={formState.town}
                          onChange={(event) => setFormState((prev) => ({ ...prev, town: event.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Suburb"
                          fullWidth
                          value={formState.suburb}
                          onChange={(event) => setFormState((prev) => ({ ...prev, suburb: event.target.value }))}
                        />
                      </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  )}
                  {tutorStep === 3 && (
                    <Grid item xs={12}>
                      <Box sx={{ width: '100%', maxWidth: 960, mx: 'auto' }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          sx={{
                            width: '100%',
                            minWidth: 260,
                            '& .MuiInputBase-root': { width: '100%', minWidth: 260 },
                            '& .MuiSelect-select': { width: '100%', minWidth: 260 }
                          }}
                        >
                          <InputLabel id="tutoring-options-label">Tutoring options</InputLabel>
                          <Select
                            labelId="tutoring-options-label"
                            multiple
                            value={formState.tutoringOptions}
                            onChange={(event) => setFormState((prev) => ({ ...prev, tutoringOptions: event.target.value }))}
                            input={<OutlinedInput label="Tutoring options" />}
                            renderValue={(selected) => (selected.length ? selected.join(', ') : 'None')}
                            SelectDisplayProps={{ style: { width: '100%', minWidth: 260 } }}
                          >
                            {tutoringChoices.map((option) => (
                              <MenuItem key={option} value={option}>
                                <Checkbox checked={formState.tutoringOptions.includes(option)} />
                                <ListItemText primary={option.replace(/^\w/, (c) => c.toUpperCase())} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      {formDialog.mode === 'create' && (
                        <>
                          <Grid item xs={12}>
                            <TextField
                              label="Password"
                              type="password"
                              fullWidth
                              value={formState.password}
                              onChange={(event) => setFormState((prev) => ({ ...prev, password: event.target.value }))}
                              error={Boolean(formErrors.password)}
                              helperText={formErrors.password || 'Minimum 8 characters.'}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              label="Confirm password"
                              type="password"
                              fullWidth
                              value={formState.confirmPassword}
                              onChange={(event) => setFormState((prev) => ({ ...prev, confirmPassword: event.target.value }))}
                              error={Boolean(formErrors.confirmPassword)}
                              helperText={formErrors.confirmPassword}
                            />
                          </Grid>
                        </>
                      )}
                        </Grid>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              )}
            </Stack>
          ) : isAdminCreate ? (
            <Stack spacing={2}>
              <Typography variant="h6" component="h3" fontWeight={800}>
                Admin details
              </Typography>
              <Grid container spacing={2} sx={{ maxWidth: 720, mx: 'auto', width: '100%' }}>
                <Grid item xs={12}>
                  <TextField
                    label="Name"
                    fullWidth
                    value={formState.name}
                    onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
                    error={Boolean(formErrors.name)}
                    helperText={formErrors.name || 'Enter the admin display name.'}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Email"
                    fullWidth
                    value={formState.email}
                    onChange={(event) => setFormState((prev) => ({ ...prev, email: event.target.value }))}
                    error={Boolean(formErrors.email)}
                    helperText={formErrors.email}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Cell number"
                    fullWidth
                    value={formState.phone}
                    onChange={(event) => setFormState((prev) => ({ ...prev, phone: event.target.value }))}
                    error={Boolean(formErrors.phone)}
                    helperText={formErrors.phone}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Password"
                    type="password"
                    fullWidth
                    value={formState.password}
                    onChange={(event) => setFormState((prev) => ({ ...prev, password: event.target.value }))}
                    error={Boolean(formErrors.password)}
                    helperText={formErrors.password || 'Minimum 8 characters.'}
                  />
                </Grid>
              </Grid>
            </Stack>
          ) : (
            <Grid container spacing={2} sx={{ maxWidth: 1100, mx: 'auto', width: '100%' }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="First name"
                fullWidth
                value={formState.name}
                onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
                error={Boolean(formErrors.name)}
                helperText={formErrors.name}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Surname"
                fullWidth
                value={formState.surname}
                onChange={(event) => setFormState((prev) => ({ ...prev, surname: event.target.value }))}
                error={Boolean(formErrors.surname)}
                helperText={formErrors.surname}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Email"
                fullWidth
                value={formState.email}
                onChange={(event) => setFormState((prev) => ({ ...prev, email: event.target.value }))}
                error={Boolean(formErrors.email)}
                helperText={formErrors.email}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="role-label">Role</InputLabel>
                <Select
                  labelId="role-label"
                  label="Role"
                  value={formState.role}
                  onChange={(event) => {
                    const role = event.target.value;
                    const keepAcademicFields = role === 'student' || role === 'tutor';
                    setFormState((prev) => ({
                      ...prev,
                      role,
                      tutorIds: role === 'student' ? prev.tutorIds : [],
                      subjectIds: keepAcademicFields ? prev.subjectIds : [],
                      grade: keepAcademicFields ? prev.grade : '',
                      portals: role === 'tutor' ? normalizeTutorPortals(prev.portals) : [],
                      activePortal: role === 'tutor' ? pickFirstPortal(prev.portals, prev.activePortal || 'tutor') : '',
                    }));
                  }}
                >
                  <MenuItem value="student">Student</MenuItem>
                  <MenuItem value="tutor">Tutor</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="superadmin">Superadmin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formState.role !== 'tutor' && <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                sx={{
                  width: '100%',
                  minWidth: 260,
                  '& .MuiInputBase-root': { width: '100%', minWidth: 260 },
                  '& .MuiSelect-select': { width: '100%', minWidth: 260 }
                }}
              >
                <InputLabel id="program-label">Program</InputLabel>
                <Select
                  labelId="program-label"
                  label="Program"
                  value={formState.program}
                  onChange={(event) => setFormState((prev) => ({ ...prev, program: event.target.value }))}
                  SelectDisplayProps={{ style: { width: '100%', minWidth: 260 } }}
                >
                  <MenuItem value="general">General</MenuItem>
                  <MenuItem value="math">Math</MenuItem>
                </Select>
              </FormControl>
            </Grid>}
              {formState.role === 'student' && (
                <>
                  <Grid item xs={12} md={6}>
                          <FormControl
                            fullWidth
                            error={Boolean(formErrors.grade)}
                            sx={{
                              width: '100%',
                              minWidth: 260,
                              '& .MuiInputBase-root': { width: '100%', minWidth: 260 },
                              '& .MuiSelect-select': { width: '100%', minWidth: 260 }
                            }}
                          >
                            <InputLabel id="grade-label">Grade</InputLabel>
                            <Select
                              labelId="grade-label"
                              label="Grade"
                              value={formState.grade}
                              onChange={(event) => setFormState((prev) => ({ ...prev, grade: event.target.value }))}
                              SelectDisplayProps={{ style: { width: '100%', minWidth: 260 } }}
                            >
                      {gradeOptions.map((grade) => (
                        <MenuItem key={grade} value={grade}>
                          {grade}
                        </MenuItem>
                      ))}
                    </Select>
                    {formErrors.grade && <FormHelperText>{formErrors.grade}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TutorDetailsAutocomplete
                    label="Search and allocate tutors"
                    value={formState.tutorIds}
                    options={availableTutorOptions}
                    allTutors={allTutors}
                    subjectsCatalog={subjects}
                    error={Boolean(formErrors.tutorIds)}
                    helperText={formErrors.tutorIds}
                    onChange={(tutorIds) => setFormState((prev) => ({ ...prev, tutorIds }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    error={Boolean(formErrors.subjectIds)}
                    sx={{
                      width: '100%',
                      minWidth: 260,
                      '& .MuiInputBase-root': { width: '100%', minWidth: 260 },
                      '& .MuiSelect-select': { width: '100%', minWidth: 260 }
                    }}
                  >
                    <InputLabel id="subjects-label">Subjects</InputLabel>
                    <Select
                      labelId="subjects-label"
                      multiple
                      value={formState.subjectIds}
                      onChange={(event) => setFormState((prev) => ({ ...prev, subjectIds: event.target.value }))}
                      input={<OutlinedInput label="Subjects" />}
                      renderValue={(selected) => {
                        if (!selected.length) return 'None';
                        return selected
                          .map((id) => {
                            const match = subjects.find((subject) => String(subject._id) === String(id));
                            return match ? match.name : null;
                          })
                          .filter(Boolean)
                          .join(', ');
                      }}
                      SelectDisplayProps={{ style: { width: '100%', minWidth: 260 } }}
                    >
                      {curriculumFilteredSubjects.map((subject) => (
                        <MenuItem key={subject._id} value={subject._id}>
                          <Checkbox checked={formState.subjectIds.includes(subject._id)} />
                          <ListItemText primary={subject.name} />
                        </MenuItem>
                      ))}
                    </Select>
                    {formErrors.subjectIds && <FormHelperText>{formErrors.subjectIds}</FormHelperText>}
                  </FormControl>
                  </Grid>
                </>
              )}
              {formState.role === 'tutor' && (
                <>
                  <Grid item xs={12} md={6}>
                    <FormControl
                      fullWidth
                      error={Boolean(formErrors.grade)}
                      sx={{
                        width: '100%',
                        minWidth: 260,
                        '& .MuiInputBase-root': { width: '100%', minWidth: 260 },
                        '& .MuiSelect-select': { width: '100%', minWidth: 260 }
                      }}
                      SelectDisplayProps={{ style: { width: '100%', minWidth: 260 } }}
                    >
                    <InputLabel id="tutor-grade-label">Grade</InputLabel>
                    <Select
                      labelId="tutor-grade-label"
                      label="Grade"
                      value={formState.grade}
                      onChange={(event) => setFormState((prev) => ({ ...prev, grade: event.target.value }))}
                      SelectDisplayProps={{ style: { width: '100%', minWidth: 260 } }}
                    >
                        {gradeOptions.map((grade) => (
                          <MenuItem key={grade} value={grade}>
                            {grade}
                          </MenuItem>
                        ))}
                      </Select>
                      {formErrors.grade && <FormHelperText>{formErrors.grade}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth error={Boolean(formErrors.subjectIds)}>
                      <InputLabel id="tutor-subjects-label">Subjects</InputLabel>
                      <Select
                        labelId="tutor-subjects-label"
                        multiple
                        value={formState.subjectIds}
                        onChange={(event) => setFormState((prev) => ({ ...prev, subjectIds: event.target.value }))}
                        input={<OutlinedInput label="Subjects" />}
                        renderValue={(selected) => {
                          if (!selected.length) return 'None';
                          return selected
                            .map((id) => {
                              const match = subjects.find((subject) => String(subject._id) === String(id));
                              return match ? match.name : null;
                            })
                            .filter(Boolean)
                            .join(', ');
                        }}
                      >
                        {curriculumFilteredSubjects.map((subject) => (
                          <MenuItem key={subject._id} value={subject._id}>
                            <Checkbox checked={formState.subjectIds.includes(subject._id)} />
                            <ListItemText primary={subject.name} />
                          </MenuItem>
                        ))}
                      </Select>
                      {formErrors.subjectIds && <FormHelperText>{formErrors.subjectIds}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth error={Boolean(formErrors.portals)} disabled={formState.role === 'tutor'}>
                      <InputLabel id="role-tutor-portals-label">Portal Access</InputLabel>
                      <Select
                        labelId="role-tutor-portals-label"
                        multiple
                        value={normalizeTutorPortals(formState.portals)}
                        onChange={(event) => {
                          const portals = normalizeTutorPortals(event.target.value);
                          setFormState((prev) => ({
                            ...prev,
                            portals,
                            activePortal: pickFirstPortal(portals, prev.activePortal || 'tutor'),
                          }));
                        }}
                        input={<OutlinedInput label="Portal Access" />}
                        renderValue={(selected) =>
                          selected
                            .map((portal) => tutorPortalOptions.find((option) => option.value === portal)?.label || portal)
                            .join(', ')
                        }
                      >
                        {tutorPortalOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            <Checkbox checked={normalizeTutorPortals(formState.portals).includes(option.value)} />
                            <ListItemText primary={option.label} />
                          </MenuItem>
                        ))}
                      </Select>
                      {formErrors.portals && <FormHelperText>{formErrors.portals}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth error={Boolean(formErrors.activePortal)} disabled={formState.role === 'tutor'}>
                      <InputLabel id="role-tutor-active-portal-label">Default Active Portal</InputLabel>
                      <Select
                        labelId="role-tutor-active-portal-label"
                        label="Default Active Portal"
                        value={pickFirstPortal(formState.portals, formState.activePortal || 'tutor')}
                        onChange={(event) =>
                          setFormState((prev) => ({ ...prev, activePortal: String(event.target.value || '').toLowerCase() }))
                        }
                      >
                        {normalizeTutorPortals(formState.portals).map((portal) => (
                          <MenuItem key={portal} value={portal}>
                            {tutorPortalOptions.find((option) => option.value === portal)?.label || portal}
                          </MenuItem>
                        ))}
                      </Select>
                      {formErrors.activePortal && <FormHelperText>{formErrors.activePortal}</FormHelperText>}
                    </FormControl>
                  </Grid>
                </>
              )}

              {formDialog.mode === 'create' && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                    label="Password"
                    type="password"
                    fullWidth
                    value={formState.password}
                    onChange={(event) => setFormState((prev) => ({ ...prev, password: event.target.value }))}
                    error={Boolean(formErrors.password)}
                    helperText={formErrors.password || 'Minimum 8 characters.'}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Confirm password"
                    type="password"
                    fullWidth
                    value={formState.confirmPassword}
                    onChange={(event) => setFormState((prev) => ({ ...prev, confirmPassword: event.target.value }))}
                    error={Boolean(formErrors.confirmPassword)}
                    helperText={formErrors.confirmPassword}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                label="Phone number"
                fullWidth
                value={formState.phone}
                onChange={(event) => setFormState((prev) => ({ ...prev, phone: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Country"
                fullWidth
                value={formState.country}
                onChange={(event) => setFormState((prev) => ({ ...prev, country: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Province"
                fullWidth
                value={formState.province}
                onChange={(event) => setFormState((prev) => ({ ...prev, province: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Town / City"
                fullWidth
                value={formState.town}
                onChange={(event) => setFormState((prev) => ({ ...prev, town: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Suburb"
                fullWidth
                value={formState.suburb}
                onChange={(event) => setFormState((prev) => ({ ...prev, suburb: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
                          <FormControl
                            fullWidth
                            error={Boolean(formErrors.tutoringOptions)}
                            sx={{
                              width: '100%',
                              minWidth: 260,
                              '& .MuiInputBase-root': { width: '100%', minWidth: 260 },
                              '& .MuiSelect-select': { width: '100%', minWidth: 260 }
                            }}
                          >
                <InputLabel id="tutoring-options-label">Tutoring options</InputLabel>
                <Select
                  labelId="tutoring-options-label"
                  multiple
                  value={formState.tutoringOptions}
                  onChange={(event) => setFormState((prev) => ({ ...prev, tutoringOptions: event.target.value }))}
                  input={<OutlinedInput label="Tutoring options" />}
                  renderValue={(selected) => (selected.length ? selected.join(', ') : 'None')}
                  SelectDisplayProps={{ style: { width: '100%' } }}
                >
                  {tutoringChoices.map((option) => (
                    <MenuItem key={option} value={option}>
                      <Checkbox checked={formState.tutoringOptions.includes(option)} />
                      <ListItemText primary={option.replace(/^\w/, (c) => c.toUpperCase())} />
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.tutoringOptions && <FormHelperText>{formErrors.tutoringOptions}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ width: '100%' }}>
              <FormControl
                fullWidth
                sx={{
                  width: '100%',
                  minWidth: 260,
                  '& .MuiInputBase-root': { width: '100%', minWidth: 260 },
                  '& .MuiSelect-select': { width: '100%', minWidth: 260 }
                }}
              >
                <InputLabel id="curricula-label">Curricula</InputLabel>
                <Select
                  labelId="curricula-label"
                  multiple
                  value={formState.curricula}
                  onChange={(event) => setFormState((prev) => ({ ...prev, curricula: event.target.value }))}
                  input={<OutlinedInput label="Curricula" />}
                  renderValue={(selected) => (selected.length ? selected.join(', ') : 'None')}
                  SelectDisplayProps={{ style: { width: '100%', minWidth: 260 } }}
                >
                  {curriculumOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      <Checkbox checked={formState.curricula.includes(option)} />
                      <ListItemText primary={option} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {formState.role === 'tutor' && (
              <>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Tutor profile standard fields
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="ID / passport / driver license number"
                    fullWidth
                    value={formState.identityNumber}
                    onChange={(event) => setFormState((prev) => ({ ...prev, identityNumber: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Date of birth"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={formState.dateOfBirth}
                    onChange={(event) => setFormState((prev) => ({ ...prev, dateOfBirth: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel id="edit-tutor-gender-label">Gender</InputLabel>
                    <Select
                      labelId="edit-tutor-gender-label"
                      label="Gender"
                      value={formState.gender}
                      onChange={(event) => setFormState((prev) => ({ ...prev, gender: event.target.value }))}
                    >
                      <MenuItem value="">Not specified</MenuItem>
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Private address line"
                    fullWidth
                    value={formState.addressLine1}
                    onChange={(event) => setFormState((prev) => ({ ...prev, addressLine1: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Area code"
                    fullWidth
                    value={formState.areaCode}
                    onChange={(event) => setFormState((prev) => ({ ...prev, areaCode: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Postal codes"
                    fullWidth
                    helperText="Comma separated. Used for booking matching."
                    value={formState.postalCodes}
                    onChange={(event) => setFormState((prev) => ({ ...prev, postalCodes: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Public tutor bio"
                    fullWidth
                    multiline
                    minRows={3}
                    value={formState.bio}
                    onChange={(event) => setFormState((prev) => ({ ...prev, bio: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Degree type"
                    fullWidth
                    value={formState.degreeType}
                    onChange={(event) => setFormState((prev) => ({ ...prev, degreeType: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Degree specialization"
                    fullWidth
                    value={formState.degreeSpecialization}
                    onChange={(event) => setFormState((prev) => ({ ...prev, degreeSpecialization: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    label="Institution / university"
                    fullWidth
                    value={formState.institutionName}
                    onChange={(event) => setFormState((prev) => ({ ...prev, institutionName: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Graduation year"
                    fullWidth
                    value={formState.graduationYear}
                    onChange={(event) => setFormState((prev) => ({ ...prev, graduationYear: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <GeneralTutorProfileFields formState={formState} setFormState={setFormState} subjects={subjects} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Teaching styles"
                    fullWidth
                    helperText="Comma separated"
                    value={formState.teachingStyles}
                    onChange={(event) => setFormState((prev) => ({ ...prev, teachingStyles: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Special needs support"
                    fullWidth
                    helperText="Comma separated"
                    value={formState.specialNeedsSupport}
                    onChange={(event) => setFormState((prev) => ({ ...prev, specialNeedsSupport: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formState.hasPoliceClearance}
                        onChange={(event) => setFormState((prev) => ({ ...prev, hasPoliceClearance: event.target.checked }))}
                      />
                    }
                    label="Police clearance on file"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formState.hasSaceRegistration}
                        onChange={(event) => setFormState((prev) => ({ ...prev, hasSaceRegistration: event.target.checked }))}
                      />
                    }
                    label="SACE registration"
                  />
                </Grid>
                {formState.hasSaceRegistration && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="SACE number"
                      fullWidth
                      value={formState.saceRegistrationNumber}
                      onChange={(event) => setFormState((prev) => ({ ...prev, saceRegistrationNumber: event.target.value }))}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Bank name"
                    fullWidth
                    value={formState.bankName}
                    onChange={(event) => setFormState((prev) => ({ ...prev, bankName: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Bank account name"
                    fullWidth
                    value={formState.bankAccountName}
                    onChange={(event) => setFormState((prev) => ({ ...prev, bankAccountName: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Bank account number"
                    fullWidth
                    value={formState.bankAccountNumber}
                    onChange={(event) => setFormState((prev) => ({ ...prev, bankAccountNumber: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="PayPal account email"
                    fullWidth
                    value={formState.paypalAccountEmail}
                    onChange={(event) => setFormState((prev) => ({ ...prev, paypalAccountEmail: event.target.value }))}
                  />
                </Grid>
              </>
            )}
          </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {isStudentCreate ? (
            <>
              <Button
                onClick={() => {
                  if (!formDialog.loading) {
                    setFormDialog({ open: false, mode: 'create', loading: false });
                    resetForm();
                  }
                }}
                disabled={formDialog.loading}
              >
                Cancel
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button onClick={handleStudentBack} disabled={studentStep === 0 || formDialog.loading}>
                Back
              </Button>
              {studentStep < 4 ? (
                <Button variant="contained" onClick={handleStudentNext} disabled={formDialog.loading}>
                  Next
                </Button>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    onClick={() => setStudentEditSummary((prev) => !prev)}
                    disabled={formDialog.loading}
                  >
                    {studentEditSummary ? 'Review' : 'Edit'}
                  </Button>
                  <Button variant="contained" onClick={handleSubmitForm} disabled={formDialog.loading}>
                    {formDialog.loading ? 'Saving...' : 'Confirm'}
                  </Button>
                </>
              )}
            </>
          ) : isTutorCreate ? (
            <>
              <Button
                onClick={() => {
                  if (!formDialog.loading) {
                    setFormDialog({ open: false, mode: 'create', loading: false });
                    resetForm();
                  }
                }}
                disabled={formDialog.loading}
              >
                Cancel
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button onClick={handleTutorBack} disabled={tutorStep === 0 || formDialog.loading}>
                Back
              </Button>
              {tutorStep < 4 ? (
                <Button variant="contained" onClick={handleTutorNext} disabled={formDialog.loading}>
                  Next
                </Button>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    onClick={() => setTutorEditSummary((prev) => !prev)}
                    disabled={formDialog.loading}
                  >
                    {tutorEditSummary ? 'Review' : 'Edit'}
                  </Button>
                  <Button variant="contained" onClick={handleSubmitForm} disabled={formDialog.loading}>
                    {formDialog.loading ? 'Saving...' : 'Confirm'}
                  </Button>
                </>
              )}
            </>
          ) : isAdminCreate ? (
            <>
              <Button
                onClick={() => {
                  if (!formDialog.loading) {
                    setFormDialog({ open: false, mode: 'create', loading: false });
                    resetForm();
                  }
                }}
                disabled={formDialog.loading}
              >
                Cancel
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button variant="contained" onClick={handleSubmitForm} disabled={formDialog.loading}>
                {formDialog.loading ? 'Saving...' : 'Confirm'}
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => {
                  if (!formDialog.loading) {
                    setFormDialog({ open: false, mode: 'create', loading: false });
                    resetForm();
                  }
                }}
                disabled={formDialog.loading}
              >
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSubmitForm} disabled={formDialog.loading}>
                {formDialog.loading ? 'Saving...' : formDialog.mode === 'create' ? 'Create user' : 'Save changes'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      <Dialog
        open={portalRequirementDialog.open}
        onClose={() => setPortalRequirementDialog({ open: false, requirements: [] })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Additional Fields Required</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              The selected portal access requires additional information before this user can be saved.
            </Typography>
            {portalRequirementDialog.requirements.map((item) => (
              <Paper key={item.portal} variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {tutorPortalOptions.find((option) => option.value === item.portal)?.label || item.portal}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Required fields: {item.requiredFields.join(', ')}
                </Typography>
              </Paper>
            ))}
            {missingTutoringOptions && (
              <FormControl fullWidth error={Boolean(formErrors.tutoringOptions)}>
                <InputLabel id="portal-requirement-tutoring-options-label">Tutoring options</InputLabel>
                <Select
                  labelId="portal-requirement-tutoring-options-label"
                  multiple
                  value={formState.tutoringOptions}
                  onChange={(event) =>
                    setFormState((prev) => ({
                      ...prev,
                      tutoringOptions: event.target.value,
                    }))
                  }
                  input={<OutlinedInput label="Tutoring options" />}
                  renderValue={(selected) => (selected.length ? selected.join(', ') : 'None')}
                >
                  {tutoringChoices.map((option) => (
                    <MenuItem key={option} value={option}>
                      <Checkbox checked={formState.tutoringOptions.includes(option)} />
                      <ListItemText primary={option.replace(/^\w/, (c) => c.toUpperCase())} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPortalRequirementDialog({ open: false, requirements: [] })}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handlePortalRequirementContinue}
            disabled={missingTutoringOptions && !formState.tutoringOptions.length}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      <UserProfileDialog
        open={viewDialog.open}
        onClose={() => setViewDialog({ open: false, user: null, applicationDocuments: null, applicationDocumentsLoading: false, applicationDocumentsError: '' })}
        user={viewDialog.user}
        title="User profile"
        getDisplayName={getDisplayName}
        getTutorNames={(user) => getTutorNames(user?.tutorIds)}
        getSubjectNames={(user) => getSubjectNames(user?.subjectIds)}
        applicationDocuments={viewDialog.applicationDocuments}
        applicationDocumentsLoading={viewDialog.applicationDocumentsLoading}
        applicationDocumentsError={viewDialog.applicationDocumentsError}
        onOpenApplicationDocument={openTutorApplicationDocument}
      />

      <Dialog
        open={passwordDialog.open}
        onClose={() => {
          if (!passwordDialog.loading) {
            setPasswordDialog({ open: false, user: null, loading: false, newPassword: '', confirmPassword: '' });
          }
        }}
      >
        <DialogTitle>Change password</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="New password"
              type="password"
              value={passwordDialog.newPassword}
              onChange={(event) => setPasswordDialog((prev) => ({ ...prev, newPassword: event.target.value }))}
              helperText="Minimum 8 characters."
            />
            <TextField
              label="Confirm new password"
              type="password"
              value={passwordDialog.confirmPassword}
              onChange={(event) => setPasswordDialog((prev) => ({ ...prev, confirmPassword: event.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setPasswordDialog({ open: false, user: null, loading: false, newPassword: '', confirmPassword: '' })
            }
            disabled={passwordDialog.loading}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleChangePassword} disabled={passwordDialog.loading}>
            {passwordDialog.loading ? 'Updating...' : 'Update password'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={resendDialog.open}
        onClose={() => {
          if (!resendDialog.loading) {
            setResendDialog({ open: false, user: null, loading: false, password: '' });
          }
        }}
      >
        <DialogTitle>Resend welcome email</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Optionally include a new password in the email.
          </Typography>
          <TextField
            label="Temporary password (optional)"
            fullWidth
            value={resendDialog.password}
            onChange={(event) => setResendDialog((prev) => ({ ...prev, password: event.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setResendDialog({ open: false, user: null, loading: false, password: '' })}
            disabled={resendDialog.loading}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleResendWelcome} disabled={resendDialog.loading}>
            {resendDialog.loading ? 'Sending...' : 'Send email'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDialog.open}
        onClose={() => {
          if (!confirmDialog.loading) {
            setConfirmDialog({ open: false, title: '', description: '', loading: false, onConfirm: null });
          }
        }}
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">{confirmDialog.description}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmDialog({ open: false, title: '', description: '', loading: false, onConfirm: null })
            }
            disabled={confirmDialog.loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              if (confirmDialog.onConfirm) {
                await confirmDialog.onConfirm();
              }
            }}
            disabled={confirmDialog.loading}
          >
            {confirmDialog.loading ? 'Working...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default Users;
